/*
  File: src/components/Stream/WatchBottomMetadataOverlay.jsx
  Purpose: Full-featured ticker overlay for sports/match content with player enrichment
  Responsibilities:
  - Maintain a queue of incoming metadata and animate each item across the ticker
  - Open a quick product grid panel and a detailed product panel from metadata
  - Integrate with cart via `useCart` to add selected variants to cart
  - Enrich player data with cached information and team-based recommendations
  Props:
  - metadata: single item, array, or { items } collection to enqueue
  - isFullscreen?: ensure overlay stays on top during fullscreen
  Notes:
  - Uses REACT_APP_API_BASE to fetch products by SKU derived from metadata
  - Supports player cache and team-based product recommendations
*/
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Coins } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaypal } from '@fortawesome/free-brands-svg-icons';
// removed processing success icon import
import { useAuth } from '../../auth/AuthContext';
import { apiClient } from '../../auth/api';
import Loader from '../common/Loader';
import ProductImageGallery from '../product/ImagePanel';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../cart/CartContext';
import DetectionsConfig from '../../config/detections';
import { getCachedPlayers, setCachedPlayers } from '../../utils/playerCache';

const MARQUEE_DURATION_MS = 8000;

const WatchBottomMetadataOverlay = ({ metadata, isFullscreen = false, customLabelsConfig = null, onPanelVisibilityChange = null, sidebarOpen = false, rightMenuWidth = 0 }) => {
    // Queue of incoming metadata items
    const [queue, setQueue] = useState([]);
    const [currentItem, setCurrentItem] = useState(null);
    const [currentBatch, setCurrentBatch] = useState([]); // [{ label, player }]
    const [showPanel, setShowPanel] = useState(false);
    const trackRef = useRef(null);
    const itemRef = useRef(null);
    const panelRef = useRef(null);
    const detailRef = useRef(null);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [products, setProducts] = useState([]);
    const [productError, setProductError] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedVariants, setSelectedVariants] = useState([]);
    const [selectedColor, setSelectedColor] = useState('');
    const [selectedSize, setSelectedSize] = useState('');
    const [detailLoading, setDetailLoading] = useState(false);
    const [variantImages, setVariantImages] = useState([]);
    const [variantImageIndex, setVariantImageIndex] = useState(0);
    const contentRef = useRef(null);
    const { items: cartItems, addItem, removeItem, clear, total, paymentMethod, setPaymentMethod } = useCart();
    const { user: authUser } = useAuth();
    // Fallback to localStorage for immediate avatar updates
    const localUser = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('auth:user') || 'null'); } catch { return null; }
    }, []);
    const effectiveUser = authUser || localUser;
    // Lightweight toast state for success notifications
    const [toastMsg, setToastMsg] = useState("");
    const [toastKind, setToastKind] = useState('success'); // 'success' | 'info' | 'error' (future)
    const [animConfig, setAnimConfig] = useState({ startX: 0, endX: 0, duration: 8 });
    const lastShownRef = useRef(new Map());
    // Recommended groups: [{ vendor: {id,name}, items: [...] }]
    const [recs, setRecs] = useState([]);
    const [recsLocked, setRecsLocked] = useState(false);
    // Explicit vendor filter for recommendations (derived from clicked player's team)
    const [recVendors, setRecVendors] = useState(null);
    const searchAbortRef = useRef(null);
    const [recsLoading, setRecsLoading] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [hideOtherSection, setHideOtherSection] = useState(false);
    const suppressAutoOpenRef = useRef(false);
    const kitHintRef = useRef(null);
    // Checkout state (address/payment)
    // Note: user already declared above as authUser
    const navigate = useNavigate();
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const profileBtnRef = useRef(null);
    const profileMenuRef = useRef(null);
    // paymentMethod and setter are from CartContext above
    const [addr, setAddr] = useState({ full_name: '', line1: '', line2: '', city: '', state: '', postal_code: '', country: '', phone: '' });
    const [savingAddr, setSavingAddr] = useState(false);
    const [placing, setPlacing] = useState(false);
    const [orderStatusMsg, setOrderStatusMsg] = useState('');
    const [showCheckout, setShowCheckout] = useState(false);
    const [showConfirmReview, setShowConfirmReview] = useState(false);
    const [showAddressForm, setShowAddressForm] = useState(false);
    // Crypto payment state
    const [cryptoOpen, setCryptoOpen] = useState(false);
    const [cryptoInvoice, setCryptoInvoice] = useState(null);
    const [cryptoAmountLoading, setCryptoAmountLoading] = useState(false);
    const [btcPriceUsd, setBtcPriceUsd] = useState(null);
    const [btcPriceLoading, setBtcPriceLoading] = useState(false);
    const [btcPriceError, setBtcPriceError] = useState('');
    // Fetch current BTC price in USD when crypto is selected
    useEffect(() => {
        const loadBtcPrice = async () => {
            if (paymentMethod !== 'crypto') return;
            try {
                setBtcPriceLoading(true);
                setBtcPriceError('');
                const { data } = await apiClient.get('/crypto/btc/price');
                const price = Number(data?.price_usd);
                if (Number.isFinite(price) && price > 0) {
                    setBtcPriceUsd(price);
                } else {
                    setBtcPriceUsd(null);
                    setBtcPriceError('Unable to load BTC price');
                }
            } catch (e) {
                setBtcPriceUsd(null);
                setBtcPriceError('Failed to fetch BTC price');
            } finally {
                setBtcPriceLoading(false);
            }
        };
        loadBtcPrice();
    }, [paymentMethod]);

    // animation stage removed
    const addressSummary = useMemo(() => {
        const parts = [
            addr.full_name,
            addr.line1,
            addr.line2,
            [addr.city, addr.state].filter(Boolean).join(', '),
            [addr.postal_code, addr.country].filter(Boolean).join(' '),
        ].filter((v) => String(v || '').trim());
        return parts.join(' • ');
    }, [addr]);
    const [excludeSelectedFromReview, setExcludeSelectedFromReview] = useState(false);
    // Categories cache (fallback when category_details is missing)
    const [categories, setCategories] = useState([]);
    // Do not re-show the same player/team/number combo within this window
    const DEDUP_ENABLED = !!DetectionsConfig.ignoreRepeatEnabled;
    const DEDUP_WINDOW_MS = Math.max(0, (DetectionsConfig.ignoreRepeatSeconds ?? 10) * 1000);
    const DEBUG_TICKER = !!DetectionsConfig.overlayTickerDebug;

    // Push incoming metadata to queue (supports arrays or { items: [] })
    useEffect(() => {
        if (!metadata) return;
        const now = Date.now();
        const extractKitHint = (m) => {
            try {
                if (!m || typeof m !== 'object') return null;
                if (typeof m.v === 'string') return m.v.toLowerCase();

                const teamsArr = Array.isArray(m.teams) ? m.teams : [];
                if (teamsArr.length > 0) {
                    const instList = Array.isArray(teamsArr[0]?.i) ? teamsArr[0].i : (Array.isArray(teamsArr[0]?.instances) ? teamsArr[0].instances : []);
                    if (instList.length > 0) {
                        // Check for cl field first (new pattern: <team>_<color>_kit_<year>[_back])
                        if (typeof instList[0]?.cl === 'string') {
                            const cl = String(instList[0].cl).toLowerCase();
                            // Extract color from pattern: <team>_<color>_kit_<year>[_back]
                            // or <team>_goalkeeper_<color>_kit_<year>[_back]
                            const parts = cl.split('_');
                            let colorIndex = -1;

                            // Find kit position and extract color before it
                            const kitIndex = parts.findIndex(part => part === 'kit');
                            if (kitIndex > 0) {
                                if (parts[kitIndex - 2] === 'goalkeeper') {
                                    // Pattern: <team>_goalkeeper_<color>_kit_<year>
                                    colorIndex = kitIndex - 1;
                                } else {
                                    // Pattern: <team>_<color>_kit_<year>
                                    colorIndex = kitIndex - 1;
                                }

                                if (colorIndex >= 0 && colorIndex < parts.length) {
                                    const color = parts[colorIndex];
                                    // Map common color variations
                                    if (color === 'home') return 'home';
                                    if (color === 'away') return 'away';
                                    if (color === 'third') return 'third';
                                    return color; // Return raw color if not standard
                                }
                            }
                        }

                        // Fallback to v field (old pattern)
                        if (typeof instList[0]?.v === 'string') return String(instList[0].v).toLowerCase();
                    }
                }
            } catch { }
            return null;
        };
        const kitHint = extractKitHint(metadata);
        if (kitHint) kitHintRef.current = kitHint;
        const toArray = (m) => {
            if (Array.isArray(m)) return m;
            if (Array.isArray(m?.items)) return m.items;
            return [m];
        };
        const items = toArray(metadata).map((m, idx) => ({ ...m, _id: now + idx }));
        if (items.length) setQueue((prev) => {
            const next = [...prev, ...items];
            return next.length > 200 ? next.slice(-200) : next;
        });
        // Interrupt current display if configured
        if (!isFullscreen && DetectionsConfig.overlayTickerInterruptOnNew) {
            setCurrentItem(null);
            setCurrentBatch([]);
        }
    }, [metadata, isFullscreen]);

    // Helper: parse a metadata entry into {team, player, number}
    const parseEntry = (m) => {
        if (!m) return {};
        let team = m.team;
        let player = m.player;
        let number = m.number;
        const teamsArray = Array.isArray(m.teams) ? m.teams : [];
        if (teamsArray.length > 0) {
            const primary = teamsArray[0] || {};
            const instances = Array.isArray(primary.i) ? primary.i : (Array.isArray(primary.instances) ? primary.instances : []);
            const teamFromNew = primary.t || primary.team;
            let playerFromNew;
            let numberFromNew;
            if (instances.length > 0) {
                const inst0 = instances[0] || {};
                playerFromNew = inst0.p || inst0.player;
                const n = inst0.n;
                if (n && typeof n === 'object' && !Array.isArray(n)) numberFromNew = n.t ?? n.value;
                else if (Array.isArray(n) && n.length > 0) {
                    const first = n[0];
                    numberFromNew = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
                }
            } else {
                playerFromNew = primary.p || primary.player;
                if (Array.isArray(primary.n) && primary.n.length > 0) {
                    const first = primary.n[0];
                    numberFromNew = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
                } else if (typeof primary.n === 'number' || typeof primary.n === 'string') numberFromNew = primary.n;
            }
            team = team ?? teamFromNew;
            player = player ?? playerFromNew;
            if (typeof number === 'undefined') number = numberFromNew;
        }
        return { team, player, number };
    };

    const makeLabel = (f) => {
        if (!f) return '';
        const hasPlayer = !!(f.player && String(f.player).trim());
        const hasNumber = !(f.number === undefined || f.number === null || String(f.number) === '');
        if (!hasPlayer && !hasNumber) return '';
        let s = '';
        if (hasPlayer) s += String(f.player);
        if (hasNumber) s += `${hasPlayer ? ' ' : ''}${String(f.number)}`;
        if (f.team) s += ` (${String(f.team)})`;
        return s;
    };

    // Extract multiple detections (team/player/number combos) from a single metadata entry
    const extractCombos = (m) => {
        const out = [];
        if (!m || typeof m !== 'object') return out;
        // Legacy root-level fields
        if (m.team || m.player || typeof m.number !== 'undefined' || m.sku) {
            out.push({ team: m.team, player: m.player, number: m.number, sku: m.sku, cl: m.cl || undefined, v: m.v || undefined, origin: m.origin });
        }
        const arr = Array.isArray(m.teams) ? m.teams : [];
        arr.forEach((t) => {
            const team = t?.t || t?.team || '';
            const instList = Array.isArray(t?.i) ? t.i : (Array.isArray(t?.instances) ? t.instances : []);
            if (instList.length > 0) {
                instList.forEach((inst) => {
                    let num;
                    const n = inst?.n;
                    if (n && typeof n === 'object' && !Array.isArray(n)) num = n.t ?? n.value;
                    else if (Array.isArray(n) && n.length > 0) {
                        const first = n[0];
                        num = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
                    }
                    out.push({ team, player: inst?.p || inst?.player, number: num, cl: inst?.cl, v: inst?.v });
                });
            } else {
                // Fallback to team-level fields
                let num;
                const n = t?.n;
                if (n && Array.isArray(n) && n.length > 0) {
                    const first = n[0];
                    num = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
                } else if (typeof n === 'number' || typeof n === 'string') num = n;
                out.push({ team, player: t?.p || t?.player, number: num, cl: undefined, v: undefined });
            }
        });
        return out;
    };

    // Batch enrich multiple combos via POST /players/search (number-then-name) with caching
    const enrichCombosBatch = async (filters) => {
        const base = process.env.REACT_APP_API_BASE || '/dev';
        try {
            const queries = filters.map((f) => {
                const teamRaw = f.team ? String(f.team).toLowerCase() : '';
                // For face_only, search only by player name and omit team entirely
                if (teamRaw === 'face_only') {
                    const names = f.player ? [String(f.player)] : [];
                    return { names };
                }
                const tokens = [];
                if (typeof f.number !== 'undefined' && f.number !== null && String(f.number) !== '') tokens.push(String(f.number));
                if (f.player) tokens.push(String(f.player));
                return { team: teamRaw || null, names: tokens };
            });

            // If cache disabled, fetch fresh each time to avoid expired image URLs
            if (!DetectionsConfig.playerCacheEnabled) {
                const controller = new AbortController();
                const prev = searchAbortRef.current;
                if (prev) { try { prev.abort(); } catch { } }
                searchAbortRef.current = controller;
                const res = await fetch(`${base}/players/search`, {
                    method: 'POST',
                    headers: { 'content-type': 'application/json', accept: 'application/json' },
                    body: JSON.stringify({ queries }),
                    signal: controller.signal,
                });
                if (!res.ok) return filters.map(() => null);
                const data = await res.json();
                const items = Array.isArray(data?.items) ? data.items : [];
                return filters.map((f, idx) => {
                    const first = items[idx];
                    if (!first) return null;
                    return {
                        ...f,
                        image_url: first.image_url || undefined,
                        display_name: first.display_name || (Array.isArray(first.names) ? first.names[0] : undefined) || f.player,
                        team: first.team || f.team,
                        display_team: first.team || f.team,
                        position: first.position || undefined,
                        // Prefer detection number; otherwise take player_number from search result
                        number: (f.number !== undefined && f.number !== null && String(f.number) !== '') ? f.number : (first.player_number != null ? String(first.player_number) : undefined),
                        player_id: first.id
                    };
                });
            }

            // Cache lookup
            const cached = getCachedPlayers(queries);
            const result = new Array(filters.length).fill(null);
            const need = [];
            const needIdx = [];
            cached.forEach((c, i) => {
                if (c && (c.id || c.display_name || c.image_url)) {
                    result[i] = {
                        ...filters[i],
                        image_url: c.image_url || undefined,
                        display_name: c.display_name || (Array.isArray(c.names) ? c.names[0] : undefined) || filters[i].player,
                        team: c.team || filters[i].team,
                        display_team: c.team || filters[i].team,
                        position: c.position || undefined,
                        // Prefer detection number; otherwise take player_number from cache item
                        number: (filters[i].number !== undefined && filters[i].number !== null && String(filters[i].number) !== '') ? filters[i].number : (c.player_number != null ? String(c.player_number) : undefined),
                        player_id: c.id,
                    };
                } else {
                    need.push(queries[i]);
                    needIdx.push(i);
                }
            });

            if (need.length > 0) {
                const controller2 = new AbortController();
                const prev2 = searchAbortRef.current;
                if (prev2) { try { prev2.abort(); } catch { } }
                searchAbortRef.current = controller2;
                const res = await fetch(`${base}/players/search`, {
                    method: 'POST',
                    headers: { 'content-type': 'application/json', accept: 'application/json' },
                    body: JSON.stringify({ queries: need }),
                    signal: controller2.signal,
                });
                if (!res.ok) return result;
                const data = await res.json();
                const items = Array.isArray(data?.items) ? data.items : [];
                setCachedPlayers(need, items);
                items.forEach((first, j) => {
                    const idx = needIdx[j];
                    if (!first) return;
                    result[idx] = {
                        ...filters[idx],
                        image_url: first.image_url || undefined,
                        display_name: first.display_name || (Array.isArray(first.names) ? first.names[0] : undefined) || filters[idx].player,
                        team: first.team || filters[idx].team,
                        display_team: first.team || filters[idx].team,
                        position: first.position || undefined,
                        // Prefer detection number; otherwise take player_number from search result
                        number: (filters[idx].number !== undefined && filters[idx].number !== null && String(filters[idx].number) !== '') ? filters[idx].number : (first.player_number != null ? String(first.player_number) : undefined),
                        player_id: first.id
                    };
                });
            }
            return result;
        } catch (_e) {
            // On error, do not fallback to cache when disabled
            if (_e && _e.name === 'AbortError') return filters.map(() => null);
            if (!DetectionsConfig.playerCacheEnabled) return filters.map(() => null);
            const queriesFallback = filters.map((f) => ({ team: f.team ? String(f.team).toLowerCase() : null, names: [f.number, f.player].filter(Boolean) }));
            const cached2 = getCachedPlayers(queriesFallback);
            return cached2.map((c, i) => c ? ({
                ...filters[i],
                image_url: c.image_url || undefined,
                display_name: c.display_name || (Array.isArray(c.names) ? c.names[0] : undefined) || filters[i].player,
                team: c.team || filters[i].team,
                display_team: c.team || filters[i].team,
                position: c.position || undefined,
                number: (filters[i].number !== undefined && filters[i].number !== null && String(filters[i].number) !== '') ? filters[i].number : (c.player_number != null ? String(c.player_number) : undefined),
                player_id: c.id,
            }) : null);
        }
    };

    // Static ticker: maintain a visible tiles row capped at 5 (small) or 7 (fullscreen)
    const [tiles, setTiles] = useState([]);
    const tilesRef = useRef([]);
    useEffect(() => { tilesRef.current = tiles; }, [tiles]);

    // Add window resize listener to recalculate tiles when screen size changes
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const highlightEnabled = !!(DetectionsConfig.overlayTickerHighlightEnabled ?? DetectionsConfig.highlight_enabled);
    const cubeMs = Number(DetectionsConfig.overlayTickerCubeMs ?? 600);

    // Assign consistent frame colors per team (up to two primary teams per stream)
    const teamColorMapRef = useRef(new Map()); // teamKey -> { borderClass, bgClass }
    // Custom label colors: label -> { color, team }
    const customLabelMapRef = useRef(new Map());
    // Per-team label map: teamKey -> Map(label -> color)
    const teamLabelMapRef = useRef(new Map());
    // Team logos: teamKey -> logoUrl
    const teamLogoMapRef = useRef(new Map());
    // Stable two-team keys to keep colors from mixing
    const knownTeamKeysRef = useRef([]);
    useEffect(() => {
        const map = new Map();
        const teamMap = new Map();
        const logoMap = new Map();
        try {
            const cfg = customLabelsConfig;
            // Accept either { teams: [...] } or a plain object { teamKey: [ {label,color}, ... ], ... }
            let teams = [];
            if (cfg && Array.isArray(cfg.teams)) {
                teams = cfg.teams;
            } else if (cfg && typeof cfg === 'object') {
                const keys = Object.keys(cfg).filter(k => Array.isArray(cfg[k]));
                teams = keys.map(k => ({ team: k, custom_labels: cfg[k] }));
            }
            teams.forEach(t => {
                const team = (t?.team || '').toString().toLowerCase();
                const arr = Array.isArray(t?.custom_labels) ? t.custom_labels : [];
                const perTeam = new Map();
                arr.forEach(cl => {
                    const label = (cl?.label || '').toString().toLowerCase();
                    const color = (cl?.color || '').toString();
                    if (label) {
                        map.set(label, { color, team });
                        perTeam.set(label, color);
                    }
                });
                if (team) teamMap.set(team, perTeam);
            });
            // Extract logos if provided
            const logos = (cfg && (cfg.logos || cfg.team_logos)) || null;
            const s3ToHttp = (url) => {
                try {
                    if (typeof url !== 'string') return url;
                    if (url.startsWith('http://') || url.startsWith('https://')) return url;
                    if (!url.startsWith('s3://')) return url;
                    const without = url.replace('s3://', '');
                    const slash = without.indexOf('/');
                    if (slash <= 0) return url;
                    const bucket = without.slice(0, slash);
                    const key = without.slice(slash + 1);
                    return `https://${bucket}.s3.amazonaws.com/${key}`;
                } catch { return url; }
            };
            if (logos && typeof logos === 'object') {
                Object.entries(logos).forEach(([k, v]) => {
                    let tk = String(k || '').toLowerCase();
                    if (!v) return;
                    const url = s3ToHttp(v);
                    const base = tk.replace(/[_-](logo|log)$/i, '');
                    logoMap.set(base, url);
                    logoMap.set(tk, url);
                });
            } else if (cfg && typeof cfg === 'object') {
                // Fallback: scan top-level keys for direct logo URL strings
                Object.entries(cfg).forEach(([k, v]) => {
                    if (typeof v === 'string' && /^(https?:\/\/|s3:\/\/)/i.test(v)) {
                        let tk = String(k || '').toLowerCase();
                        const url = s3ToHttp(v);
                        const base = tk.replace(/[_-](logo|log)$/i, '');
                        logoMap.set(base, url);
                        logoMap.set(tk, url);
                    }
                });
            }
        } catch (_) { /* ignore */ }
        customLabelMapRef.current = map;
        teamLabelMapRef.current = teamMap;
        teamLogoMapRef.current = logoMap;
    }, [customLabelsConfig]);
    // Palette for assigning a stable team color. We keep neutral frame classes
    // and use the hex color here only for the jersey number badge.
    const teamPalette = useRef([
        { borderClass: 'border-white/30', bgClass: 'bg-white/10', hex: '#34d399' }, // emerald-400
        { borderClass: 'border-white/30', bgClass: 'bg-white/10', hex: '#38bdf8' }, // sky-400
        { borderClass: 'border-white/30', bgClass: 'bg-white/10', hex: '#e879f9' }, // fuchsia-400
        { borderClass: 'border-white/30', bgClass: 'bg-white/10', hex: '#fb923c' }, // orange-400
    ]);
    const getTeamFrame = (entry) => {
        try {
            // Normalize team key and read metadata custom label (cl)
            const rawTeam = String(entry?.display_team || entry?.team || '').trim().toLowerCase();
            const cl = String(entry?.custom_label || '').trim().toLowerCase();
            const isGK = typeof entry?.position === 'string' && entry.position.toLowerCase().includes('goalkeeper');
            // Use global normalizer and then snap to the two stable match keys
            let key = normalizeTeamKey(rawTeam);
            try {
                const stable = knownTeamKeysRef.current || [];
                if (stable.length >= 2 && key && !stable.includes(key)) {
                    const [k1, k2] = stable;
                    const s = (val) => String(val || '').replace(/[^a-z0-9]/g, '');
                    const ks = s(key), k1s = s(k1), k2s = s(k2);
                    if (ks.includes(k1s) || k1s.includes(ks)) key = k1;
                    else if (ks.includes(k2s) || k2s.includes(ks)) key = k2;
                    else key = (Math.abs(ks.length - k1s.length) <= Math.abs(ks.length - k2s.length)) ? k1 : k2;
                }
            } catch { }
            // If a custom label is attached, prefer the color defined for THIS team only
            let labelInfo = null;
            if (cl && key) {
                try {
                    const teamLabels = teamLabelMapRef.current.get(key);
                    if (teamLabels && teamLabels.has(cl)) {
                        labelInfo = { color: teamLabels.get(cl), team: key };
                    }
                } catch { /* ignore */ }
            }
            // Guard: if the label points to a goalkeeper kit but the entry is not a goalkeeper, ignore this label
            try {
                const clIsGK = cl.includes('goalkeeper') || /\bgk\b/.test(cl);
                if (labelInfo && clIsGK && !isGK) {
                    labelInfo = null;
                }
            } catch { /* ignore */ }
            // Helper: convert a hex/named color to rgba with alpha
            const toRgba = (color, a = 0.25) => {
                const hex = (color || '').toString();
                const h = hex.replace('#', '');
                if (/^[0-9a-fA-F]{3}$/.test(h)) {
                    const r = parseInt(h[0] + h[0], 16);
                    const g = parseInt(h[1] + h[1], 16);
                    const b = parseInt(h[2] + h[2], 16);
                    return `rgba(${r}, ${g}, ${b}, ${a})`;
                }
                if (/^[0-9a-fA-F]{6}$/.test(h)) {
                    const r = parseInt(h.slice(0, 2), 16);
                    const g = parseInt(h.slice(2, 4), 16);
                    const b = parseInt(h.slice(4, 6), 16);
                    return `rgba(${r}, ${g}, ${b}, ${a})`;
                }
                const named = hex.toLowerCase();
                if (named.includes('yellow')) return 'rgba(234,179,8,0.28)';
                if (named.includes('green')) return 'rgba(34,197,94,0.28)';
                if (named.includes('blue')) return 'rgba(59,130,246,0.28)';
                if (named.includes('red')) return 'rgba(239,68,68,0.28)';
                return 'rgba(255,255,255,0.1)';
            };

            // Determine team color:
            // 1) If a known custom label is attached, use its color.
            // 2) Else, use team's Home kit color from custom labels; if the player's position is goalkeeper, use goalkeeper Home kit color.
            // 3) Else, fallback to a palette per team.
            let badgeHex = null;
            if (labelInfo && labelInfo.color) {
                badgeHex = String(labelInfo.color);
            } else if (key) {
                const teamLabels = teamLabelMapRef.current.get(key);
                if (teamLabels && teamLabels.size > 0) {
                    const findColor = (wantGK) => {
                        let exactHome = null;
                        let anyHome = null;
                        let anyOther = null;
                        for (const [lbl, clr] of teamLabels.entries()) {
                            const l = String(lbl || '').toLowerCase();
                            const isLabelGK = l.includes('goalkeeper') || /\bgk\b/.test(l);
                            const isHome = l.includes('home');
                            if (wantGK && !isLabelGK) continue;
                            if (!wantGK && isLabelGK) continue;
                            if (isHome) {
                                exactHome = clr; // prefer first home match
                                break;
                            }
                            // For back variants or others, keep as fallback
                            if (!anyHome && l.includes('kit')) anyHome = clr;
                            if (!anyOther) anyOther = clr;
                        }
                        return exactHome || anyHome || anyOther || null;
                    };
                    // Prefer GK home if player is GK; else prefer outfield home
                    const preferred = findColor(isGK) || findColor(!isGK);
                    if (preferred) badgeHex = preferred;
                }
                // Fallback to palette assignment if no team home color available
                if (!badgeHex) {
                    const map = teamColorMapRef.current;
                    const stable = knownTeamKeysRef.current || [];
                    if (stable.length >= 2 && (key === stable[0] || key === stable[1])) {
                        const idx = (key === stable[0]) ? 0 : 1;
                        const chosen = teamPalette.current[idx] || teamPalette.current[0];
                        map.set(key, chosen);
                    } else if (!map.has(key)) {
                        const palette = teamPalette.current;
                        const used = new Set(Array.from(map.values()).map(v => v.hex));
                        const next = palette.find(p => !used.has(p.hex)) || palette[0];
                        map.set(key, next);
                    }
                    const assigned = map.get(key);
                    badgeHex = assigned?.hex || '#ffffff';
                }
            }

            // Always return a neutral frame; only the badge uses the team color
            const neutral = { borderClass: 'border-white/30', bgClass: 'bg-white/10' };
            const badgeStyle = badgeHex
                ? { color: '#ffffff', fontWeight: 700, border: '1px solid rgba(255,255,255,0.35)', backgroundColor: toRgba(badgeHex, 0.5) }
                : null;
            const bgStyle = badgeHex ? { backgroundColor: toRgba(badgeHex, 0.25) } : {};
            return { ...neutral, style: {}, badgeStyle, bgStyle };
        } catch (_) {
            return { borderClass: 'border-white/30', bgClass: 'bg-white/10', style: {}, badgeStyle: null, bgStyle: {} };
        }
    };

    // Format player display name as F.Lastname (e.g., H.Kane). If only one token, keep as-is.
    const formatShortName = (n) => {
        const s = String(n || '').trim();
        if (!s) return '';
        const parts = s.split(/\s+/).filter(Boolean);
        if (parts.length < 2) return parts[0];
        const initial = parts[0].charAt(0).toUpperCase();
        const last = parts[parts.length - 1];
        return `${initial}.${last}`;
    };

    // Swap animation state for first tile (cube-like transition)
    const [swapAnim, setSwapAnim] = useState(null); // { active, from, to, key }
    const pendingSwapsRef = useRef([]);
    const swapTimerRef = useRef(null);
    // Concurrent batch swap state for replacing multiple tiles at once
    const [swapBatch, setSwapBatch] = useState(null); // { active: true, items: [{ index, from, to, key }] }
    const swapBatchTimerRef = useRef(null);
    const swapQueueRef = useRef([]);
    // Maintain FIFO order of current tiles (by key), independent of visual indices
    const ageQueueRef = useRef([]);

    const startSwapBatch = (planned) => {
        if (!Array.isArray(planned) || planned.length === 0) return;
        const prevTiles = tilesRef.current || [];
        const items = [];
        const localRest = [];
        for (let i = 0; i < planned.length; i++) {
            const newE = planned[i];
            const oldestKey = ageQueueRef.current.shift();
            if (!oldestKey) { localRest.push(newE); continue; }
            let idx = prevTiles.findIndex(t => t && t.key === oldestKey);
            if (idx < 0) idx = i % Math.max(1, prevTiles.length);
            items.push({ index: idx, from: prevTiles[idx], to: newE, key: `swap:${Date.now()}:${i}` });
            // Push new key to tail as newest
            ageQueueRef.current.push(newE.key);
        }
        swapQueueRef.current = localRest;
        setSwapBatch({ active: true, items });
        if (swapBatchTimerRef.current) { try { clearTimeout(swapBatchTimerRef.current); } catch { } }
        swapBatchTimerRef.current = setTimeout(() => {
            // Commit all replacements
            setTiles((prev) => {
                const next = prev.slice();
                items.forEach(it => { if (next[it.index]) next[it.index] = it.to; });
                return next;
            });
            setSwapBatch(null);
            const rest = swapQueueRef.current || [];
            if (rest.length > 0) {
                swapQueueRef.current = [];
                setTimeout(() => startSwapBatch(rest), 0);
            }
        }, cubeMs + 20);
    };

    // Cleanup batch timer on unmount
    useEffect(() => () => { if (swapBatchTimerRef.current) { try { clearTimeout(swapBatchTimerRef.current); } catch { } } }, []);
    const startNextSwap = () => {
        if (swapAnim && swapAnim.active) return;
        const nextTo = pendingSwapsRef.current.shift();
        if (!nextTo) { return; }
        const from = (tilesRef.current || [])[0];
        if (!from) { if (DEBUG_TICKER) console.warn('[Ticker] no from (no first tile)'); return; }
        setSwapAnim({ active: true, from, to: nextTo, key: `swap:${Date.now()}` });
    };
    const enqueueSwaps = (arr) => {
        if (!Array.isArray(arr) || arr.length === 0) return;
        pendingSwapsRef.current.push(...arr);
        startNextSwap();
    };
    const commitSwap = () => {
        setTiles((prev) => {
            if (!swapAnim?.to) return prev;
            const next = [swapAnim.to, ...prev.slice(1)];
            return next;
        });
        setSwapAnim(null);
        // trigger next swap in queue, if any
        setTimeout(() => startNextSwap(), 0);
    };

    // Fallback: if framer onAnimationComplete doesn't fire, commit after duration
    useEffect(() => {
        if (swapAnim && swapAnim.active) {
            if (swapTimerRef.current) { try { clearTimeout(swapTimerRef.current); } catch { } }
            const durationMs = 420; // should match cube transition duration
            swapTimerRef.current = setTimeout(() => {
                if (swapAnim && swapAnim.active) {
                    commitSwap();
                }
            }, durationMs + 10);
            return () => { if (swapTimerRef.current) { try { clearTimeout(swapTimerRef.current); } catch { } } };
        } else {
            if (swapTimerRef.current) { try { clearTimeout(swapTimerRef.current); } catch { } swapTimerRef.current = null; }
        }
    }, [swapAnim]);

    // Safety: if we have pending swaps and no active swap, start one
    useEffect(() => {
        if (!swapAnim || !swapAnim.active) {
            if (pendingSwapsRef.current && pendingSwapsRef.current.length > 0) {
                startNextSwap();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tiles, swapAnim]);

    // Highlight management for duplicate detections already visible
    const [highlightMap, setHighlightMap] = useState({}); // { [key]: true }
    const hlTimersRef = useRef(new Map());
    const triggerHighlight = (key) => {
        if (!highlightEnabled) return;
        try {
            setHighlightMap((prev) => ({ ...prev, [key]: true }));
            const old = hlTimersRef.current.get(key);
            if (old) clearTimeout(old);
            const duration = Number(DetectionsConfig.overlayTickerHighlightMs ?? 3000);
            const t = setTimeout(() => {
                setHighlightMap((prev) => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
                hlTimersRef.current.delete(key);
            }, Math.max(0, duration));
            hlTimersRef.current.set(key, t);
        } catch { }
    };
    useEffect(() => () => { // cleanup timers on unmount
        hlTimersRef.current.forEach((t) => clearTimeout(t));
        hlTimersRef.current.clear();
    }, []);
    useEffect(() => {
        let cancelled = false;
        if (queue.length === 0) return;
        const now = Date.now();
        const MAX_BATCH = 20;
        (async () => {
            let consume = 0;
            const candidates = [];
            const carry = [];
            while (consume < queue.length && candidates.length < MAX_BATCH * 3) {
                const entry = queue[consume];
                const combos = extractCombos(entry);
                for (let i = 0; i < combos.length; i++) {
                    const f = combos[i];
                    const hasPlayer = !!(f.player && String(f.player).trim());
                    const hasNumber = !(f.number === undefined || f.number === null || String(f.number) === '');
                    if (!hasPlayer && !hasNumber) continue;
                    if (candidates.length < MAX_BATCH) candidates.push({ f, entry });
                    else carry.push({ team: f.team, player: f.player, number: f.number });
                }
                consume += 1;
            }
            if (candidates.length === 0) {
                setQueue((prev) => prev.slice(consume));
                return;
            }
            // If all candidates are SKU labels, bypass enrichment immediately
            const allSkuLabels = candidates.every(c => String(c?.f?.origin || '').toLowerCase() === 'sku_label');
            let enrichedAll;
            if (allSkuLabels) {
                // Resolve product info for SKU labels so tiles show correct name and image
                try {
                    const results = await Promise.all(candidates.map(async (c) => {
                        const sku = c?.f?.sku || c?.f?.player || '';
                        if (!sku || typeof sku !== 'string') {
                            return {
                                ...c.f,
                                display_name: c.f.player || '',
                                team: c.f.team || '',
                                display_team: c.f.team || '',
                                player_id: undefined,
                                image_url: undefined,
                            };
                        }
                        try {
                            const { data } = await apiClient.get(`/products/by-sku/${encodeURIComponent(sku)}?include_suggested=false`);
                            const prod = data?.product || null;
                            if (prod) {
                                return {
                                    ...c.f,
                                    display_name: prod.display_name || prod.name || (c.f.player || ''),
                                    team: c.f.team || '',
                                    display_team: c.f.team || '',
                                    player_id: undefined,
                                    image_url: prod.image_url || undefined,
                                };
                            }
                        } catch { /* ignore and fallback */ }
                        return {
                            ...c.f,
                            display_name: c.f.player || '',
                            team: c.f.team || '',
                            display_team: c.f.team || '',
                            player_id: undefined,
                            image_url: undefined,
                        };
                    }));
                    enrichedAll = results;
                } catch {
                    enrichedAll = candidates.map(c => ({
                        ...c.f,
                        display_name: c.f.player || '',
                        team: c.f.team || '',
                        display_team: c.f.team || '',
                        player_id: undefined,
                        image_url: undefined,
                    }));
                }
            } else {
                enrichedAll = await enrichCombosBatch(candidates.map(p => p.f));
            }
            if (cancelled) return;
            const entries = [];
            for (let i = 0; i < enrichedAll.length; i++) {
                const enriched = enrichedAll[i];
                const src = candidates[i]?.f;
                const isLabelOrigin = String(src?.origin || '').toLowerCase();
                const e = enriched || ((src && (isLabelOrigin === 'label' || isLabelOrigin === 'sku_label')) ? ({
                    ...src,
                    display_name: src.player || '',
                    display_team: src.team || '',
                    origin: src.origin,
                    image_url: undefined,
                    player_id: undefined,
                }) : null);
                if (!e) { continue; }
                const playerKey = e.player_id ? `id:${String(e.player_id)}` : `name:${String(e.display_name || e.player || '').toLowerCase()}`;
                const isAlreadyShown = Array.isArray(tilesRef.current) && tilesRef.current.some(t => t.key === playerKey);
                if (DEDUP_ENABLED) {
                    const last = lastShownRef.current.get(playerKey);
                    if (last && now - last < DEDUP_WINDOW_MS) {
                        continue;
                    }
                }
                if (isAlreadyShown) { // even outside dedup window, highlight and skip adding
                    triggerHighlight(playerKey);
                    continue;
                }
                const name = e.display_name || e.player || '';
                const hasNumber = !(e.number === undefined || e.number === null || String(e.number) === '');
                const teamShort = e.display_team || e.team || '';
                const label = `${name}${hasNumber ? ' ' + String(e.number) : ''}${teamShort ? ` (${teamShort})` : ''}`.trim();
                entries.push({
                    key: playerKey,
                    label,
                    player: e.player,
                    team: e.team,
                    number: e.number,
                    image_url: e.image_url,
                    display_name: e.display_name,
                    display_team: teamShort,
                    player_id: e.player_id,
                    position: e.position || undefined,
                    custom_label: (candidates[i]?.f?.cl) || undefined,
                    variant: (candidates[i]?.f?.v) || undefined,
                    sku: (candidates[i]?.f?.sku) || undefined,
                });
                if (DEDUP_ENABLED) lastShownRef.current.set(playerKey, now);
                if (entries.length >= MAX_BATCH) break;
            }
            setQueue((prev) => {
                const rest = prev.slice(consume);
                const out = carry.length > 0 ? [...carry, ...rest] : rest;
                return out;
            });
            if (entries.length > 0) {
                const prev = tilesRef.current || [];
                const existing = new Set(prev.map(t => t.key));
                const uniqueNew = entries.filter(e => {
                    if (existing.has(e.key)) {
                        if (DEDUP_ENABLED) {
                            const lastDup = lastShownRef.current.get(e.key);
                            if (!lastDup || now - lastDup >= DEDUP_WINDOW_MS) triggerHighlight(e.key);
                        } else {
                            triggerHighlight(e.key);
                        }
                        return false;
                    }
                    existing.add(e.key);
                    return true;
                });
                const capacity = Math.max(0, dynamicMaxTiles - prev.length);
                const toAppend = uniqueNew.slice(0, capacity);
                const plannedSwaps = uniqueNew.slice(capacity);
                if (toAppend.length > 0) {
                    setTiles(prevState => prevState.concat(toAppend));
                    // Update FIFO queue with newly appended keys at the tail
                    try { ageQueueRef.current.push(...toAppend.map(e => e.key)); } catch { }
                }
                if (plannedSwaps.length > 0) {
                    // Start a concurrent cube batch for the first N tiles
                    startSwapBatch(plannedSwaps);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [queue, isFullscreen]);


    const containerClass = isFullscreen
        ? "fixed bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3 z-[2147483647] pointer-events-auto"
        : "absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3 z-50 pointer-events-auto";

    const hasContent = tiles.length > 0;

    // Use configurable styling
    const tickerHeight = Number(isFullscreen ? (DetectionsConfig.overlayTickerFullscreenHeightPx ?? 68) : (DetectionsConfig.overlayTickerHeightPx ?? 44));
    const avatarPx = Number(isFullscreen ? (DetectionsConfig.overlayTickerAvatarFullscreenPx ?? 52) : (DetectionsConfig.overlayTickerAvatarPx ?? 36));
    const fontClass = String(isFullscreen ? (DetectionsConfig.overlayTickerFontClassFullscreen || 'text-sm sm:text-base') : (DetectionsConfig.overlayTickerFontClass || 'text-xs sm:text-sm'));

    // Helper to identify jersey-like products (used by panel and badges)
    const isJerseyLike = (p) => {
        const normalize = (s) => (typeof s === 'string' ? s.toLowerCase() : '');
        const name = normalize(p?.display_name || p?.name || '');
        const hasNameHit = /\b(jersey|maillot|kit|shirt)\b/.test(name);
        let catNames = [];
        const cds = Array.isArray(p?.category_details) ? p.category_details : [];
        if (cds.length > 0) {
            catNames = cds.map(c => normalize(c?.name || ''));
        } else if (Array.isArray(p?.categories)) {
            catNames = p.categories.map(() => '');
        }
        const hasCatHit = catNames.some(n => /\b(jersey|maillot|kit|shirt)\b/.test(n));
        return hasNameHit || hasCatHit;
    };

    // Preferred kit color mapped from metadata kit hint (home/away/third)
    const preferredKitColor = useMemo(() => {
        const hint = String(kitHintRef.current || '').toLowerCase();
        if (!hint) return '';
        if (hint.includes('home')) return 'Home';
        if (hint.includes('away')) return 'Away';
        if (hint.includes('third')) return 'Third';
        return '';
    }, [kitHintRef.current]);
    const isSelectedMatchColor = useMemo(() => {
        if (!selectedProduct) return false;
        if (!isJerseyLike(selectedProduct)) return false;
        if (!preferredKitColor || !selectedColor) return false;
        return String(selectedColor).toLowerCase() === String(preferredKitColor).toLowerCase();
    }, [selectedProduct, selectedColor, preferredKitColor]);

    // Currently selected variant for primary product (by color/size)
    const selectedVariant = useMemo(() => {
        const vars = Array.isArray(selectedVariants) ? selectedVariants : [];
        if (vars.length === 0) return null;
        // Exact match color+size
        const exact = vars.find(v => (selectedColor ? String(v.color) === String(selectedColor) : true) && (selectedSize ? String(v.size) === String(selectedSize) : false));
        if (exact) return exact;
        // Any with selected color
        if (selectedColor) {
            const anyColor = vars.find(v => String(v.color) === String(selectedColor));
            if (anyColor) return anyColor;
        }
        // Fallback to first
        return vars[0];
    }, [selectedVariants, selectedColor, selectedSize]);

    // Available sizes union for current color/product
    const availableSizes = useMemo(() => {
        const vars = Array.isArray(selectedVariants) ? selectedVariants : [];
        const forColor = vars.filter(v => (selectedColor ? String(v.color) === String(selectedColor) : true));
        const set = new Set();
        forColor.forEach(v => { if (v.size) set.add(String(v.size)); });
        forColor.forEach(v => { if (Array.isArray(v.sizes)) v.sizes.forEach(s => set.add(String(s))); });
        if (Array.isArray(selectedProduct?.sizes)) selectedProduct.sizes.forEach(s => set.add(String(s)));
        const list = Array.from(set);
        return list.map(label => {
            const v = forColor.find(x => String(x.size) === String(label));
            const stock = v ? (v.stock ?? 0) : 1;
            return { label, stock };
        });
    }, [selectedVariants, selectedColor, selectedProduct]);

    // Helper to extract an array of image objects { image_url } from various shapes
    const extractDetailImages = (obj) => {
        const out = [];
        const pushUrl = (u) => { if (u && typeof u === 'string') out.push({ image_url: u }); };
        try {
            if (!obj || typeof obj !== 'object') return out;
            // Support both array and object-based images shapes
            const imgsField = obj.images;
            if (imgsField && !Array.isArray(imgsField) && typeof imgsField === 'object') {
                const pri = imgsField.primary;
                const oth = imgsField.others;
                const priUrl = pri?.image_url || pri?.url || pri?.src;
                if (priUrl) out.push({ image_url: priUrl, is_primary: true });
                if (Array.isArray(oth)) {
                    oth.forEach((it) => {
                        const u = it?.image_url || it?.url || it?.src || (typeof it === 'string' ? it : null);
                        if (u) out.push({ image_url: u });
                    });
                }
            }
            const tryArrays = [obj.images, obj.gallery, obj.media, obj.photos, obj.pictures];
            for (const arr of tryArrays) {
                if (Array.isArray(arr)) {
                    arr.forEach((it) => {
                        const u = it?.image_url || it?.url || it?.src || (typeof it === 'string' ? it : null);
                        pushUrl(u);
                    });
                }
            }
            // Variant-specific images
            if (Array.isArray(obj.variants)) {
                const matchVar = obj.variants.find(v => String(v.id) === String(selectedVariant?.id)) || obj.variants.find(v => String(v.color) === String(selectedColor));
                if (matchVar) {
                    // Variant images can be an object with primary/others or an array of objects
                    if (matchVar.images && !Array.isArray(matchVar.images) && typeof matchVar.images === 'object') {
                        const pri = matchVar.images.primary;
                        const oth = matchVar.images.others;
                        const priUrl = pri?.image_url || pri?.url || pri?.src;
                        if (priUrl) out.push({ image_url: priUrl, is_primary: true });
                        if (Array.isArray(oth)) {
                            oth.forEach((it) => {
                                const u = it?.image_url || it?.url || it?.src || (typeof it === 'string' ? it : null);
                                if (u) out.push({ image_url: u });
                            });
                        }
                    }
                    const va = [matchVar.images, matchVar.gallery, matchVar.media].find(a => Array.isArray(a)) || [];
                    va.forEach((it) => pushUrl(it?.image_url || it?.url || it?.src));
                    // Single image field
                    if (matchVar?.image?.image_url) pushUrl(matchVar.image.image_url);
                }
            }
            // Single-image fallbacks
            if (obj?.image?.image_url) pushUrl(obj.image.image_url);
            if (obj?.image_url) pushUrl(obj.image_url);
        } catch { /* ignore */ }
        // Deduplicate while preserving order
        const seen = new Set();
        return out.filter(it => { const k = it.image_url; if (!k || seen.has(k)) return false; seen.add(k); return true; });
    };

    // Load variant images for the selected product.
    // Prefer extracting from the already loaded selectedProduct to avoid duplicate network requests;
    // fall back to GET /products/<id> only if needed.
    useEffect(() => {
        if (!selectedProduct) { setVariantImages([]); setVariantImageIndex(0); return; }
        let cancelled = false;

        const computeFromLocal = (itemLike) => {
            let imgs = [];
            try {
                if (Array.isArray(itemLike?.variants)) {
                    const matchVar = itemLike.variants.find(v => String(v.id) === String(selectedVariant?.id))
                        || itemLike.variants.find(v => String(v.color) === String(selectedColor));
                    if (matchVar) imgs = extractDetailImages(matchVar);
                }
            } catch { }
            if (!Array.isArray(imgs) || imgs.length === 0) imgs = extractDetailImages(itemLike);
            if (!Array.isArray(imgs) || imgs.length === 0) imgs = extractDetailImages(selectedVariant || selectedProduct);
            // Do NOT pull images from other colors/variants; keep images restricted to the same selected color/variant.
            if (!Array.isArray(imgs) || imgs.length === 0) {
                const fallback = [];
                if (selectedVariant?.image?.image_url) fallback.push({ image_url: selectedVariant.image.image_url });
                if (selectedProduct?.image_url) fallback.push({ image_url: selectedProduct.image_url });
                imgs = fallback;
            }
            let primaryIdx = 0;
            try {
                const byFlag = imgs.findIndex(it => it && (it.is_primary || it.primary));
                if (byFlag >= 0) primaryIdx = byFlag;
                else if (selectedVariant?.image?.image_url) {
                    const byUrl = imgs.findIndex(it => it?.image_url === selectedVariant.image.image_url);
                    if (byUrl >= 0) primaryIdx = byUrl;
                }
            } catch { }
            return { imgs, primaryIdx };
        };

        // First try compute from selectedProduct (which may already be a full product with variants)
        const local = computeFromLocal(selectedProduct);
        if (Array.isArray(local.imgs) && local.imgs.length > 0) {
            if (!cancelled) {
                setVariantImages(local.imgs);
                setVariantImageIndex(local.primaryIdx);
            }
            return () => { cancelled = true; };
        }

        // Fallback: fetch the full product details once
        (async () => {
            try {
                const idToFetch = selectedProduct.id;
                const { data } = await apiClient.get(`/products/${idToFetch}`);
                if (cancelled) return;
                const item = data?.items?.[0] ?? data ?? {};
                const { imgs, primaryIdx } = computeFromLocal(item);
                setVariantImages(imgs);
                setVariantImageIndex(primaryIdx);
            } catch {
                const { imgs } = computeFromLocal(null);
                setVariantImages(imgs);
                setVariantImageIndex(0);
            }
        })();

        return () => { cancelled = true; };
    }, [selectedProduct, selectedVariant, selectedColor]);

    // Hint the browser to preconnect/preload CloudFront origins for faster image load
    useEffect(() => {
        try {
            if (!Array.isArray(variantImages) || variantImages.length === 0) return;
            const first = variantImages[Math.min(variantImageIndex, variantImages.length - 1)]?.image_url || variantImages[0]?.image_url;
            if (!first) return;
            const addLinkOnce = (rel, attrs) => {
                try {
                    const key = JSON.stringify({ rel, ...attrs });
                    const id = `hint-${btoa(key).replace(/=+/g, '')}`;
                    if (document.getElementById(id)) return;
                    const link = document.createElement('link');
                    link.id = id;
                    link.rel = rel;
                    Object.entries(attrs || {}).forEach(([k, v]) => link.setAttribute(k, v));
                    document.head.appendChild(link);
                } catch { }
            };
            const origin = new URL(first, window.location.href).origin;
            addLinkOnce('preconnect', { href: origin, crossorigin: '' });
            addLinkOnce('dns-prefetch', { href: origin });
            addLinkOnce('preload', { href: first, as: 'image' });
        } catch { }
    }, [variantImages, variantImageIndex]);

    // Keep FIFO tracker in sync if lengths diverge (fallback safety)
    useEffect(() => {
        try {
            if ((ageQueueRef.current || []).length !== tiles.length) {
                ageQueueRef.current = tiles.map(t => t.key);
            }
        } catch { }
    }, [tiles]);

    // Team filter toggle state and helpers
    const [teamFilter, setTeamFilter] = useState(null); // null = both teams, otherwise canonical team key
    const [teamToggleOpen, setTeamToggleOpen] = useState(false); // controls expanded/collapsed state of the right-edge toggle
    const teamToggleRef = useRef(null);
    const teamDropdownRef = useRef(null);
    const overlayRef = useRef(null);
    const [teamDropdownStyle, setTeamDropdownStyle] = useState(null);
    const computeTeamDropdownStyle = () => {
        try {
            const right = Math.max(12, (sidebarOpen ? (12 + Number(rightMenuWidth || 0)) : 12) + 12); // inward nudge
            const overlayEl = overlayRef.current;
            const overlayH = overlayEl ? (overlayEl.offsetHeight || overlayEl.clientHeight || 0) : 0;
            const gapY = isFullscreen ? 8 : 6;
            const bottom = Math.max(40, overlayH + gapY); // place just above the overlay container
            return { right, bottom };
        } catch { /* ignore */ }
        return { right: Math.max(12, (sidebarOpen ? (12 + Number(rightMenuWidth || 0)) : 12) + 12), bottom: isFullscreen ? 100 : 80 };
    };
    useEffect(() => {
        if (!teamToggleOpen) return;
        const update = () => setTeamDropdownStyle(computeTeamDropdownStyle());
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [teamToggleOpen, isFullscreen, sidebarOpen, rightMenuWidth]);
    useEffect(() => {
        const onDocClick = (e) => {
            try {
                if (!teamToggleOpen) return;
                const btn = teamToggleRef.current;
                const menu = teamDropdownRef.current;
                const t = e.target;
                if ((btn && btn.contains(t)) || (menu && menu.contains(t))) return;
                setTeamToggleOpen(false);
            } catch { }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [teamToggleOpen]);
    const normalizeTeamKey = (raw) => {
        const k = String(raw || '').trim().toLowerCase();
        if (!k) return '';
        const tmap = teamLabelMapRef.current || new Map();
        if (tmap.has(k)) return k;
        const simple = k.replace(/[^a-z0-9]/g, '');
        let best = '';
        for (const tk of tmap.keys()) {
            const tkSimple = tk.replace(/[^a-z0-9]/g, '');
            if (simple === tkSimple) { best = tk; break; }
            if (!best && (simple.includes(tkSimple) || tkSimple.includes(simple))) best = tk;
        }
        if (!best) {
            if (simple.includes('parissaintgermain') && (teamLabelMapRef.current || new Map()).has('psg')) best = 'psg';
            else if (simple.includes('bayernm') && (teamLabelMapRef.current || new Map()).has('bayern')) best = 'bayern';
        }
        return best || k;
    };
    // Build a readable short label for a team key using current tiles or fallback to uppercase key
    const teamLabelForKey = (teamKey) => {
        try {
            const fk = normalizeTeamKey(teamKey);
            const sample = (tiles || []).find(t => normalizeTeamKey(t.display_team || t.team) === fk);
            const raw = sample?.display_team || sample?.team || teamKey || '';
            const s = String(raw).trim();
            if (!s) return String(teamKey || '').toUpperCase();
            // If s looks like an acronym (e.g., psg), uppercase
            if (/^[a-zA-Z]{2,5}$/.test(s) && s === s.toLowerCase()) return s.toUpperCase();
            // Build acronym from words (Paris Saint-Germain -> PSG)
            const parts = s.replace(/[^a-zA-Z\s-]/g, '').split(/[\s-]+/).filter(Boolean);
            if (parts.length >= 2) {
                const ac = parts.map(p => p[0]).join('').toUpperCase();
                if (ac.length >= 2 && ac.length <= 5) return ac;
            }
            // Fallback to first word capitalized
            return parts.length ? parts[parts.length - 1] : s.toUpperCase();
        } catch { return String(teamKey || '').toUpperCase(); }
    };

    const teamOptions = useMemo(() => {
        const out = [];
        try {
            const tmap = teamLabelMapRef.current || new Map();
            for (const [team, labels] of tmap.entries()) {
                if (!labels || labels.size === 0) continue;
                let chosen = '';
                let kitType = '';

                // Priority order: home > away > third > any non-goalkeeper kit
                const kitPriorities = ['home', 'away', 'third'];

                // First, try to find kits in priority order
                for (const priority of kitPriorities) {
                    for (const [lbl, clr] of labels.entries()) {
                        const l = String(lbl || '').toLowerCase();
                        if (l.includes('goalkeeper')) continue; // Skip goalkeeper kits
                        if (l.includes(priority)) {
                            chosen = clr;
                            kitType = priority;
                            break;
                        }
                    }
                    if (chosen) break; // Found a kit with this priority, stop searching
                }

                // If no priority kit found, take any non-goalkeeper kit
                if (!chosen) {
                    for (const [lbl, clr] of labels.entries()) {
                        const l = String(lbl || '').toLowerCase();
                        if (l.includes('goalkeeper')) continue;
                        chosen = clr;
                        kitType = 'other';
                        break;
                    }
                }

                if (!chosen) continue; // Skip teams without any valid kit colors
                out.push({ key: team, color: chosen, label: teamLabelForKey(team), kitType });
                if (out.length >= 2) break;
            }
        } catch { }
        try {
            if (knownTeamKeysRef.current.length === 0 && out.length >= 2) {
                const k1 = normalizeTeamKey(out[0].key);
                const k2 = normalizeTeamKey(out[1].key);
                knownTeamKeysRef.current = [k1, k2];
                // Deterministically pre-assign palette colors for these two keys
                const map = teamColorMapRef.current;
                const palette = teamPalette.current;
                if (!map.has(k1)) map.set(k1, palette[0]);
                if (!map.has(k2)) map.set(k2, palette[1] || palette[0]);
            }
        } catch { }
        return out;
    }, [customLabelsConfig, tiles]);

    // Dynamic max tiles calculation - use config values and let CSS truncation handle long names
    const dynamicMaxTiles = useMemo(() => {
        const avatarSize = isFullscreen
            ? Number(DetectionsConfig.overlayTickerAvatarFullscreenPx ?? 52)
            : Number(DetectionsConfig.overlayTickerAvatarPx ?? 36);

        const gapSize = windowWidth >= 640 ? 28 : 20; // sm:gap-7 vs gap-5
        const avatarTextGap = 8; // gap-2
        const teamButtonSpace = teamOptions.length >= 2 ? (isFullscreen ? 52 : 44) : 0;
        const containerPadding = windowWidth >= 640 ? 24 : 16; // sm:px-3 vs px-2 (both sides)
        const safetyMargin = 10; // Reduced safety margin - just enough to prevent overlap

        // Use config values as target - CSS truncation will handle long names
        const configMaxTiles = isFullscreen
            ? Number(DetectionsConfig.overlayTickerMaxPlayersFullscreen ?? 9)
            : Number(DetectionsConfig.overlayTickerMaxPlayers ?? 8);

        // Calculate available space per tile and see if config target fits
        const availableWidth = windowWidth - containerPadding - teamButtonSpace - safetyMargin;
        const spaceForGaps = gapSize * (configMaxTiles - 1);
        const spaceForContent = availableWidth - spaceForGaps;
        const spacePerTile = spaceForContent / configMaxTiles;
        const textSpacePerTile = spacePerTile - avatarSize - avatarTextGap;

        // If we have at least 30px for text per tile, use config target
        if (textSpacePerTile >= 30) {
            return configMaxTiles;
        }

        // Otherwise, find the maximum that fits with at least 30px text space
        let adjustedTiles = configMaxTiles - 1;
        while (adjustedTiles >= 2) {
            const spaceForGapsAdjusted = gapSize * (adjustedTiles - 1);
            const spaceForContentAdjusted = availableWidth - spaceForGapsAdjusted;
            const spacePerTileAdjusted = spaceForContentAdjusted / adjustedTiles;
            const textSpacePerTileAdjusted = spacePerTileAdjusted - avatarSize - avatarTextGap;

            if (textSpacePerTileAdjusted >= 30) {
                return adjustedTiles;
            }
            adjustedTiles--;
        }

        return Math.max(2, adjustedTiles);
    }, [isFullscreen, teamOptions.length, windowWidth]);

    const tilesShown = useMemo(() => {
        if (!teamFilter) return tiles;
        const fk = normalizeTeamKey(teamFilter);
        return tiles.filter(t => normalizeTeamKey(t.display_team || t.team) === fk);
    }, [tiles, teamFilter]);

    // Load recommended product variants by team vendors via /product/recommended
    useEffect(() => {
        if (recsLocked) {
            return; // when we have suggestions from by-sku, don't fetch generic recs
        }
        let cancelled = false;
        const baseVendors = (() => {
            try {
                const keys = Array.from((teamLabelMapRef.current || new Map()).keys());
                return keys;
            } catch { return []; }
        })();
        const vendors = (() => {
            // Highest priority: explicit recommendation vendors driven by player click
            if (Array.isArray(recVendors) && recVendors.length > 0) return recVendors;
            // Otherwise follow the team filter toggle, if set
            if (teamFilter) return [normalizeTeamKey(teamFilter)];
            // Fallback to all known vendors/teams from labels config
            return baseVendors;
        })().filter(Boolean);
        if (vendors.length === 0) {
            setRecs([]);
            return;
        }
        setRecsLoading(true);
        (async () => {
            try {
                const params = { vendors: vendors.join(','), limit: 100 };
                // Build exclude_categories from both the currently shown product and all player's products (Other Products)
                const excludeSet = new Set();
                const addCatNames = (p) => {
                    try {
                        const cds = Array.isArray(p?.category_details) ? p.category_details : [];
                        for (const c of cds) {
                            const n = (c?.name || '').toString().trim().toLowerCase();
                            if (n) excludeSet.add(n);
                        }
                    } catch { /* ignore */ }
                };
                if (selectedProduct) addCatNames(selectedProduct);
                try {
                    const arr = Array.isArray(products) ? products : [];
                    for (const p of arr) addCatNames(p);
                } catch { /* ignore */ }
                if (excludeSet.size === 0 && selectedProduct && isJerseyLike(selectedProduct)) excludeSet.add('jerseys');
                if (excludeSet.size > 0) params.exclude_categories = Array.from(excludeSet).join(',');
                const { data } = await apiClient.get('/product/recommended', { params });
                if (cancelled) return;
                // Prefer category-based grouping when available from the API
                const catGroups = Array.isArray(data?.categories) ? data.categories : [];
                if (catGroups.length > 0) {
                    const normCats = catGroups.map((cg) => {
                        const items = Array.isArray(cg?.items) ? cg.items : [];
                        const mapped = items.map((it) => ({
                            id: it.product_id,
                            variant_id: it.variant_id,
                            sku: it.sku,
                            name: it.display_name || it.name || it.sku || 'Product',
                            display_name: it.display_name || undefined,
                            image_url: it.image_url || '',
                            currency: it.currency || 'USD',
                            price_cents: it.price_cents != null ? it.price_cents : 0,
                            _variantColor: it.color || undefined,
                        }));
                        return { title: (cg?.category?.name || 'Category'), items: mapped };
                    });
                    setRecs(normCats);
                } else {
                    // Fallback to vendor grouping (legacy response shape)
                    const groups = Array.isArray(data?.groups) ? data.groups : [];
                    const normGroups = groups.map(g => {
                        const vend = g?.vendor || {};
                        const items = Array.isArray(g?.items) ? g.items : [];
                        const mapped = items.map((it) => ({
                            id: it.product_id, // open product detail by product id
                            variant_id: it.variant_id,
                            sku: it.sku,
                            name: it.display_name || it.name || it.sku || 'Product',
                            display_name: it.display_name || undefined,
                            image_url: it.image_url || '',
                            currency: it.currency || 'USD',
                            price_cents: it.price_cents != null ? it.price_cents : 0,
                            _variantColor: it.color || undefined,
                        }));
                        return { title: (vend.name || 'Team'), items: mapped };
                    });
                    setRecs(normGroups);
                }
            } catch (_e) {
                if (!cancelled) setRecs([]);
            } finally {
                if (!cancelled) setRecsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [teamFilter, customLabelsConfig, recVendors, selectedProduct, products, recsLocked]);

    const formatted = useMemo(() => {
        if (!currentItem) return null;

        // Default (legacy) fields
        let team = currentItem.team;
        let player = currentItem.player;
        let number = currentItem.number;
        const event = currentItem.event;
        const description = currentItem.description;

        // Support new schemas:
        // 1) Compact: { teams: [ { t, i: [ { p, n:{t}, ... } ] } ] }
        // 2) Older new: { teams: [ { t, p, n } ] }
        const teamsArray = Array.isArray(currentItem.teams) ? currentItem.teams : [];
        if (teamsArray.length > 0) {
            const primary = teamsArray[0] || {};
            const teamFromNew = primary.t || primary.team;

            // Prefer nested instance
            const instances = Array.isArray(primary.i) ? primary.i : (Array.isArray(primary.instances) ? primary.instances : []);
            let playerFromNew;
            let numberFromNew;
            if (instances.length > 0) {
                const inst0 = instances[0] || {};
                playerFromNew = inst0.p || inst0.player;
                const n = inst0.n;
                if (n && typeof n === 'object' && !Array.isArray(n)) {
                    numberFromNew = n.t ?? n.value;
                } else if (Array.isArray(n) && n.length > 0) {
                    const first = n[0];
                    numberFromNew = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
                }
            } else {
                // Fallback to team-level fields
                playerFromNew = primary.p || primary.player;
                if (Array.isArray(primary.n) && primary.n.length > 0) {
                    const first = primary.n[0];
                    numberFromNew = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
                } else if (typeof primary.n === 'number' || typeof primary.n === 'string') {
                    numberFromNew = primary.n;
                }
            }

            team = team ?? teamFromNew;
            player = player ?? playerFromNew;
            if (typeof number === 'undefined') number = numberFromNew;
        }

        return { team, player, number, event, description };
    }, [currentItem]);

    // Cap tiles when screen mode changes or dynamic calculation updates
    useEffect(() => {
        setTiles(prev => (prev.length > dynamicMaxTiles ? prev.slice(prev.length - dynamicMaxTiles) : prev));
    }, [dynamicMaxTiles]);

    // Reset legacy animation state on fullscreen toggle (no-op for tiles)
    useEffect(() => { setCurrentItem(null); setCurrentBatch([]); }, [isFullscreen]);

    // Defer categories fetch until user opens a product (lower priority)
    useEffect(() => {
        if (!selectedProduct) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await apiClient.get('/categories', { params: { limit: 200, offset: 0 } });
                if (!cancelled) setCategories(data.items || []);
            } catch {
                if (!cancelled) setCategories([]);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedProduct]);

    const categoryById = useMemo(() => Object.fromEntries((categories || []).map(c => [c.id, c])), [categories]);

    // Build order review list = cart items + selected item (if not already in cart)
    const reviewItems = useMemo(() => {
        if (!showConfirmReview) return [];
        const base = Array.isArray(cartItems) ? cartItems : [];
        const alreadyInCart = base.some(i => String(i.id) === String(selectedProduct?.id) && (String(i.size || '') === String(selectedSize || '')));
        if (!selectedProduct || alreadyInCart || excludeSelectedFromReview) return base;
        const extra = {
            id: selectedProduct.id,
            name: selectedProduct.name || selectedProduct.display_name || '',
            display_name: selectedProduct.display_name || undefined,
            price: selectedProduct.price_cents ?? 0,
            currency: selectedProduct.currency || 'USD',
            imageUrl: selectedProduct.image_url || '',
            size: selectedSize || 'N/A',
            qty: 1,
        };
        return [extra, ...base];
    }, [showConfirmReview, cartItems, selectedProduct, selectedSize, excludeSelectedFromReview]);

    const reviewTotal = useMemo(() => {
        if (!showConfirmReview) return 0;
        const baseTotal = total || 0;
        const inCart = (Array.isArray(cartItems) ? cartItems : []).some(i => String(i.id) === String(selectedProduct?.id) && (String(i.size || '') === String(selectedSize || '')));
        const add = (!selectedProduct || inCart || excludeSelectedFromReview) ? 0 : ((selectedProduct.price_cents ?? 0) / 100);
        return Number((baseTotal + add).toFixed(2));
    }, [showConfirmReview, total, cartItems, selectedProduct, selectedSize, excludeSelectedFromReview]);

    // Load default address when opening a product
    useEffect(() => {
        if (!selectedProduct) return;
        setShowCheckout(false);
        setShowConfirmReview(false);
        setShowAddressForm(false);
        (async () => {
            try {
                const { data } = await apiClient.get('/shipping-addresses');
                if (data && data.item) setAddr({
                    full_name: data.item.full_name || '',
                    line1: data.item.line1 || '',
                    line2: data.item.line2 || '',
                    city: data.item.city || '',
                    state: data.item.state || '',
                    postal_code: data.item.postal_code || '',
                    country: data.item.country || '',
                    phone: data.item.phone || '',
                });
            } catch { }
        })();
    }, [selectedProduct]);

    const saveAddress = async () => {
        try {
            setSavingAddr(true);
            await apiClient.post('/shipping-addresses', addr);
        } catch { } finally { setSavingAddr(false); }
    };

    // Ensure the review is visible when entering the confirm step (scroll to bottom)
    useEffect(() => {
        if (showConfirmReview) {
            const el = contentRef.current;
            if (el) {
                try {
                    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                } catch (_e) {
                    el.scrollTop = el.scrollHeight;
                }
            }
        }
    }, [showConfirmReview]);

    const resetToDefaultAddress = async () => {
        try {
            const { data } = await apiClient.get('/shipping-addresses');
            if (data && data.item) setAddr({
                full_name: data.item.full_name || '',
                line1: data.item.line1 || '',
                line2: data.item.line2 || '',
                city: data.item.city || '',
                state: data.item.state || '',
                postal_code: data.item.postal_code || '',
                country: data.item.country || '',
                phone: data.item.phone || '',
            });
        } catch { }
    };

    const placeOrderNow = async () => {
        if (!effectiveUser || !selectedProduct) { setOrderStatusMsg('Please sign in to checkout.'); return; }
        // Basic validation for address
        const required = ['full_name', 'line1', 'city', 'postal_code', 'country'];
        const missing = required.filter(k => !String(addr[k] || '').trim());
        if (missing.length) { setOrderStatusMsg('Please fill required address fields.'); return; }
        try {
            setPlacing(true);
            setOrderStatusMsg('');

            // Ensure selected product is in cart (single qty)
            const alreadyInCart = (cartItems || []).some(i => String(i.id) === String(selectedProduct.id));
            if (!alreadyInCart) {
                addItem({ id: selectedProduct.id, name: selectedProduct.name || selectedProduct.display_name, price: ((selectedProduct.price_cents ?? 0) / 100), currency: selectedProduct.currency || 'USD', imageUrl: selectedProduct.image_url || '', size: selectedSize || 'N/A', qty: 1 });
            }

            // Sync current cart items to backend like CartPage
            const itemsToSync = (cartItems || []).concat(!alreadyInCart ? [{ id: selectedProduct.id, qty: 1 }] : []).map(i => ({ product_id: i.id ?? i.product_id ?? selectedProduct.id, qty: i.qty ?? 1 }));
            try {
                await apiClient.post('/carts/sync', { user_id: effectiveUser.id, items: itemsToSync });
            } catch { }

            // Fetch latest cart for user to get cart_id
            const cartsRes = await apiClient.get('/carts', { params: { user_id: effectiveUser.id, limit: 1, offset: 0 } });
            const latest = cartsRes.data?.items?.[0];
            const cart_id = latest ? latest.id : null;

            const order_uid = `ORD-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
            let providerName = 'Stripe';
            if (paymentMethod === 'paypal') providerName = 'PayPal';
            else if (paymentMethod === 'crypto') providerName = 'Crypto';

            // Build order items from cart like CartPage
            const orderItems = (cartItems || [])
                .concat(!alreadyInCart ? [{ id: selectedProduct.id, price: ((selectedProduct.price_cents ?? 0) / 100), currency: selectedProduct.currency || 'USD', qty: 1 }] : [])
                .map(i => ({ product_id: i.id, qty: i.qty ?? 1, price_cents: Math.round((i.price ?? 0) * 100), currency: i.currency || 'USD' }));

            const oRes = await apiClient.post('/orders/checkout', {
                user_id: effectiveUser.id,
                cart_id,
                order_uid,
                status: 'pending',
                items: orderItems,
                payment: {
                    provider_name: providerName,
                    provider_payment_id: order_uid,
                    amount_cents: Math.round(reviewTotal * 100),
                    currency: orderItems[0]?.currency || 'EUR',
                    status: 'pending'
                }
            });
            const createdOrderId = oRes.data?.id;
            const paymentId = oRes.data?.payment?.id;

            if (createdOrderId) {
                try {
                    await apiClient.put(`/orders/${createdOrderId}`, { status: 'completed' });
                    if (paymentId) await apiClient.put(`/payments/${paymentId}`, { status: 'done' });
                    clear();
                    setOrderStatusMsg(`Order placed successfully. ID #${createdOrderId}`);
                    // Show global toast and close checkout panel; suppress immediate reopen
                    setToastKind('success');
                    setToastMsg('Payment confirmed');
                    setTimeout(() => setToastMsg(''), 2500);
                    setShowConfirmReview(false);
                    setShowCheckout(false);
                    setSelectedProduct(null);
                    suppressAutoOpenRef.current = true;
                } catch { setOrderStatusMsg('Order placed, but failed to finalize status.'); }
            } else {
                setOrderStatusMsg('Failed to create order.');
            }
        } catch {
            setOrderStatusMsg('Failed to place order. Please try again.');
        } finally {
            setPlacing(false);
        }
    };

    // removed width lock during processing

    // Close boxes/detail panel when clicking outside
    useEffect(() => {
        if (!showPanel && !selectedProduct) return;
        const handleClickOutside = (e) => {
            const panelEl = panelRef.current;
            const itemEl = itemRef.current;
            const detailEl = detailRef.current;
            if (panelEl && panelEl.contains(e.target)) return; // clicked inside panel
            if (itemEl && itemEl.contains(e.target)) return; // clicked the ticker item (allowed)
            if (detailEl && detailEl.contains(e.target)) return; // clicked inside detail
            setShowPanel(false);
            setSelectedProduct(null);
            // Suppress auto-open to avoid immediate reopen after closing
            suppressAutoOpenRef.current = true;
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPanel, selectedProduct]);

    // Notify parent (IVSPlayer) about product panel visibility to hide player controller
    useEffect(() => {
        try {
            const open = !!(selectedProduct && !sidebarCollapsed) || !!showPanel;
            onPanelVisibilityChange && onPanelVisibilityChange(open);
        } catch (_) { }
    }, [onPanelVisibilityChange, selectedProduct, sidebarCollapsed, showPanel]);

    // Close profile menu on click outside
    useEffect(() => {
        if (!showProfileMenu) return;
        const onDoc = (e) => {
            const btn = profileBtnRef.current;
            const menu = profileMenuRef.current;
            if (btn && btn.contains(e.target)) return;
            if (menu && menu.contains(e.target)) return;
            setShowProfileMenu(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [showProfileMenu]);

    const openProductDetail = async (p, opts = {}) => {
        try {
            setDetailLoading(true);
            // Keep the current panel mounted to avoid flicker; update fields after fetch
            setSelectedVariants([]);
            setSelectedColor('');
            setSelectedSize('');
            setShowPanel(false); // hide left grid when opening detail
            // Normalize base product when clicking a synthesized jersey-variant card
            let preferColorLocal = (typeof opts.preferColor === 'string') ? opts.preferColor : (p && p._variantColor ? p._variantColor : '');
            let full = p;
            if (p && p._baseId) {
                const base = (products || []).find(x => String(x.id) === String(p._baseId));
                if (base) full = base;
            }
            if (!Array.isArray(p?.variants)) {
                try {
                    const { data } = await apiClient.get(`/products/${p.id}`);
                    full = data.items ? data.items[0] : data;
                } catch { }
            }
            const variants = Array.isArray(full?.variants) ? full.variants : [];
            setSelectedProduct(full);
            setSelectedVariants(variants);
            const colors = Array.from(new Set(variants.map(v => v.color).filter(Boolean)));
            let defaultColor = colors[0] || '';
            const preferColor = preferColorLocal;
            if (preferColor) {
                // Try exact match, then case-insensitive
                if (colors.includes(preferColor)) defaultColor = preferColor;
                else {
                    const found = colors.find(c => String(c).toLowerCase() === String(preferColor).toLowerCase());
                    if (found) defaultColor = found;
                }
            } else if (colors.length > 0) {
                // Choose random color for variety when no preference
                const idx = Math.floor(Math.random() * colors.length);
                defaultColor = colors[idx];
            }
            setSelectedColor(defaultColor);
            const candidates = variants.filter(v => (defaultColor ? v.color === defaultColor : true));
            // Build available sizes union from candidates and product-level sizes
            const sizeSet = new Set();
            candidates.forEach(v => { if (v.size) sizeSet.add(String(v.size)); });
            candidates.forEach(v => { if (Array.isArray(v.sizes)) v.sizes.forEach(s => sizeSet.add(String(s))); });
            if (Array.isArray(full?.sizes)) full.sizes.forEach(s => sizeSet.add(String(s)));
            const sizesAll = Array.from(sizeSet);
            // Default size: prefer an in-stock exact variant; else first available size
            let chosenSize = '';
            const inStockVar = candidates.find(v => (v.stock ?? 0) > 0 && v.size && sizesAll.includes(String(v.size)));
            if (inStockVar) chosenSize = String(inStockVar.size);
            else if (sizesAll.length > 0) chosenSize = String(sizesAll[0]);
            setSelectedSize(chosenSize);
            // Stay in product detail view for seamless transition
            setShowCheckout(false);
            setShowConfirmReview(false);
            // If this product is already in cart, jump straight to review
            try {
                const exists = Array.isArray(cartItems) && cartItems.some(i => String(i.id) === String(full.id));
                if (exists) setShowConfirmReview(true);
            } catch { }

            // Recommendations are now powered by /product/recommended via the dedicated effect above.
            // Reset explicit vendors to preserve any current player/team context
            setRecs([]);

            // Sync sidebar sections (Other products) to the product's primary player
            try {
                const { data: pd } = await apiClient.get(`/products/${full.id}/players`);
                const firstPlayer = Array.isArray(pd?.items) && pd.items.length > 0 ? pd.items[0] : null;
                if (firstPlayer && firstPlayer.id) {
                    // Update selectedPlayer but prevent auto-opening the first product
                    suppressAutoOpenRef.current = true;
                    setSelectedPlayer({ id: firstPlayer.id, name: firstPlayer.display_name || '', image_url: firstPlayer.image_url || '' });
                }
            } catch (_) { /* ignore */ }

            // Smooth scroll panel content to top to reveal images
            try {
                const el = contentRef.current;
                if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
            } catch { }
        } catch (_) {
            // ignore
        } finally {
            setDetailLoading(false);
        }
    };

    // When opening the checkout review, auto-scroll the panel content to the bottom to reveal the listing
    useEffect(() => {
        if (showCheckout && showConfirmReview) {
            const el = contentRef.current;
            if (!el) return;
            // Use a small timeout to wait for layout to update
            const t = setTimeout(() => {
                try { el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); } catch { }
            }, 80);
            return () => clearTimeout(t);
        }
    }, [showCheckout, showConfirmReview]);

    // Fetch products for the selected player and open the first product
    useEffect(() => {
        const playerId = selectedPlayer?.id;
        if (!playerId) return;
        setIsLoadingProducts(true);
        setProductError("");
        setProducts([]);
        apiClient.get('/products', { params: { player_id: String(playerId) } })
            .then(({ data }) => {
                let items = Array.isArray(data?.items) ? data.items : [];
                items = items.slice().sort((a, b) => {
                    const aj = isJerseyLike(a) ? 1 : 0;
                    const bj = isJerseyLike(b) ? 1 : 0;
                    if (aj !== bj) return bj - aj; // jersey first
                    // Then prefer items with images
                    const ai = a?.image_url ? 1 : 0;
                    const bi = b?.image_url ? 1 : 0;
                    if (ai !== bi) return bi - ai;
                    return 0;
                });
                setProducts(items);
                if (!suppressAutoOpenRef.current && !selectedProduct) {
                    if (items.length > 0) {
                        // If we have a kit hint (home/away/third), prefer opening the jersey with matching color
                        const hint = String(kitHintRef.current || '').toLowerCase();
                        const mapHintToColor = (h) => {
                            if (!h) return '';
                            if (h.includes('home')) return 'Home';
                            if (h.includes('away')) return 'Away';
                            if (h.includes('third')) return 'Third';
                            return '';
                        };
                        const preferColor = mapHintToColor(hint);
                        const jersey = items.find(isJerseyLike);
                        if (jersey) openProductDetail(jersey, { preferColor });
                        else openProductDetail(items[0]);
                    } else {
                        // No products for this player: show a placeholder detail panel
                        const placeholder = {
                            id: `placeholder-${String(playerId)}`,
                            display_name: selectedPlayer?.name || 'No product available',
                            name: selectedPlayer?.name || 'No product available',
                            image_url: selectedPlayer?.image_url || '',
                            price_cents: 0,
                            currency: '',
                            variants: [],
                        };
                        setSelectedVariants([]);
                        setSelectedColor('');
                        setSelectedSize('');
                        setSelectedProduct(placeholder);
                        setShowCheckout(false);
                    }
                }
            })
            .catch(() => setProductError('Failed to load products'))
            .finally(() => {
                setIsLoadingProducts(false);
            });
    }, [selectedPlayer, selectedProduct]);

    return (
        <div
            className={containerClass}
        >
            {/* Optional quick panel (left-bottom) */}
            {showPanel && (
                <div ref={panelRef} className="absolute bottom-[calc(100%+8px)] left-0 w-80 sm:w-96 bg-transparent supports-[backdrop-filter]:backdrop-blur backdrop-blur-lg border border-white/10 rounded-xl p-2 sm:p-3 text-white z-50 opacity-90 hover:opacity-100 transition-opacity">
                    <div className="mb-2 text-xs uppercase tracking-wide text-white/70">Products</div>
                    {isLoadingProducts && (
                        <Loader label="Loading products" />
                    )}
                    {!isLoadingProducts && productError && (
                        <div className="py-4 text-red-300 text-xs">{productError}</div>
                    )}
                    {!isLoadingProducts && !productError && (
                        <div className={`grid ${products.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                            {products.slice(0, 4).map((p) => (
                                <button key={p.id || p.sku}
                                    type="button"
                                    onClick={() => openProductDetail(p)}
                                    className="block text-left overflow-hidden rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
                                    <div className="aspect-[4/3] w-full bg-black/40 flex items-center justify-center overflow-hidden">
                                        {p.image_url ? (
                                            <img src={p.image_url} alt={p.display_name || p.name || p.sku}
                                                className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                        ) : (
                                            <div className="w-full h-full skeleton" aria-hidden />
                                        )}
                                    </div>
                                    <div className="p-2 text-xs truncate text-white/80">{p.display_name || p.name || p.sku}</div>
                                </button>
                            ))}
                            {products.length === 0 && (
                                <div className="text-white/60 text-xs">No products found.</div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Left-side product detail panel (compact) */}
            <AnimatePresence>
                {selectedProduct && !sidebarCollapsed && (
                    <motion.div
                        ref={detailRef}
                        initial={{ x: 32, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: 32, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 280, damping: 28 }}
                        className={`group fixed right-3 top-0 bottom-24 ${isFullscreen ? 'w-[20rem] sm:w-[22rem] md:w-[24rem]' : 'w-[16rem] sm:w-[18rem] md:w-[20rem]'
                            } max-w-[85vw] text-white z-[2147483646] shadow-2xl rounded-xl border border-white/10 overflow-hidden flex flex-col supports-[backdrop-filter]:backdrop-blur backdrop-blur-lg bg-[rgba(26,35,50,0.7)]`}
                    >
                        {/* Sticky header */}
                        <div className="sticky top-0 z-10 px-3 py-3 border-b border-white/10 bg-black/10 group-hover:bg-black/20 transition-colors">
                            {showCheckout ? (
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-semibold">{showConfirmReview ? 'Review & confirm' : 'Checkout'}</div>
                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={() => { setShowCheckout(false); setShowConfirmReview(false); setShowAddressForm(false); setOrderStatusMsg(''); }} className="text-xs px-2 py-1 rounded border border-white/20 bg-white/10 hover:bg-white/20">Back</button>
                                        <div className="relative">
                                            {(() => {
                                                const n = (effectiveUser?.name || effectiveUser?.email || '').trim();
                                                const initial = n ? n[0].toUpperCase() : 'U';
                                                return (
                                                    <button
                                                        ref={profileBtnRef}
                                                        type="button"
                                                        onClick={() => setShowProfileMenu(v => !v)}
                                                        title={n || 'Profile'}
                                                        className="w-8 h-8 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white text-sm font-semibold select-none hover:bg-white/25 overflow-hidden"
                                                    >
                                                        {effectiveUser?.avatar_url ? (
                                                            <img src={effectiveUser.avatar_url} alt="me" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <span>{initial}</span>
                                                        )}
                                                    </button>
                                                );
                                            })()}
                                            {showProfileMenu && (
                                                <div ref={profileMenuRef} className="absolute right-0 mt-2 min-w-[12rem] rounded-lg border border-white/10 bg-[rgba(26,35,50,0.95)] text-white shadow-lg p-3 z-50">
                                                    <div className="text-sm font-semibold truncate">{effectiveUser?.name || effectiveUser?.email || 'User'}</div>
                                                    <div className="text-xs text-white/70 truncate mt-0.5">Signed in</div>
                                                    <div className="mt-3 flex justify-end">
                                                        <button type="button" onClick={() => { setShowProfileMenu(false); navigate('/profile'); }} className="px-3 py-1.5 rounded bg-white/10 border border-white/20 hover:bg-white/20 text-xs">Go to profile</button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                selectedPlayer && (
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-full overflow-hidden bg-white/10 border border-white/10 flex-shrink-0">
                                                {selectedPlayer.image_url ? (
                                                    <img src={selectedPlayer.image_url} alt={selectedPlayer.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full bg-white/10" />
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-base font-semibold truncate">{selectedPlayer.name}</div>
                                                <div className="text-xs text-white/60 truncate">Live detection</div>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            {(() => {
                                                const n = (effectiveUser?.name || effectiveUser?.email || '').trim();
                                                const initial = n ? n[0].toUpperCase() : 'U';
                                                return (
                                                    <button
                                                        ref={profileBtnRef}
                                                        type="button"
                                                        onClick={() => setShowProfileMenu(v => !v)}
                                                        title={n || 'Profile'}
                                                        className="w-8 h-8 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white text-sm font-semibold select-none hover:bg-white/25 overflow-hidden"
                                                    >
                                                        {effectiveUser?.avatar_url ? (
                                                            <img src={effectiveUser.avatar_url} alt="me" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <span>{initial}</span>
                                                        )}
                                                    </button>
                                                );
                                            })()}
                                            {showProfileMenu && (
                                                <div ref={profileMenuRef} className="absolute right-0 mt-2 min-w-[12rem] rounded-lg border border-white/10 bg-[rgba(26,35,50,0.95)] text-white shadow-lg p-3 z-50">
                                                    <div className="text-sm font-semibold truncate">{effectiveUser?.name || effectiveUser?.email || 'User'}</div>
                                                    <div className="text-xs text-white/70 truncate mt-0.5">Signed in</div>
                                                    <div className="mt-3 flex justify-end">
                                                        <button type="button" onClick={() => { setShowProfileMenu(false); navigate('/profile'); }} className="px-3 py-1.5 rounded bg-white/10 border border-white/20 hover:bg-white/20 text-xs">Go to profile</button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                        <div ref={contentRef} className="flex-1 min-h-0 overflow-y-auto pt-2 sm:pt-3 pb-1 sm:pb-1 px-2 sm:px-3 scrollbar-thin">
                            {!showCheckout && (
                                <div className="flex flex-col gap-3">
                                    <div className="w-full">
                                        <ProductImageGallery images={variantImages} initialIndex={variantImageIndex} name={selectedProduct.display_name || selectedProduct.name} hideThumbs={true} transparentBackground={true} />
                                    </div>
                                    <AnimatePresence mode="wait">
                                        <motion.div key={selectedProduct?.id || selectedProduct?._baseId || 'p'} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.2 }} className="flex items-center gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold truncate text-base">{selectedProduct.display_name || selectedProduct.name}</div>
                                                <div className="mt-1 text-sm text-white/90 font-semibold">{selectedProduct.currency} {((selectedProduct.price_cents ?? 0) / 100).toFixed(2)}</div>
                                                <div className="mt-3 space-y-2">
                                                    {Array.from(new Set(selectedVariants.map(v => v.color).filter(Boolean))).length > 0 && (
                                                        <div>
                                                            <div className="mb-1 text-xs text-white/70">Color</div>
                                                            <select value={selectedColor} onChange={(e) => { setSelectedColor(e.target.value); setSelectedSize(''); }} className="w-40 px-2 py-1 rounded bg-black/40 border border-white/20 text-sm">
                                                                {Array.from(new Set(selectedVariants.map(v => v.color).filter(Boolean))).map(c => (
                                                                    <option key={c} value={c}>{c}</option>
                                                                ))}
                                                                {Array.from(new Set(selectedVariants.map(v => v.color).filter(Boolean))).length === 0 ? (
                                                                    <option value="">—</option>
                                                                ) : null}
                                                            </select>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="mb-1 text-xs text-white/70">Size</div>
                                                        <div className="flex gap-1 flex-wrap">
                                                            {availableSizes
                                                                .map(({ label, stock }) => (
                                                                    <button
                                                                        key={label}
                                                                        onClick={() => setSelectedSize(label)}
                                                                        disabled={stock <= 0}
                                                                        className={`px-2 py-1 rounded border text-xs ${selectedSize === label ? 'bg-white/20 border-white' : 'bg-white/10 border-white/20 hover:bg-white/20'} ${stock <= 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                                        title={stock <= 0 ? 'Out of stock' : ''}
                                                                    >
                                                                        {label}
                                                                    </button>
                                                                ))}
                                                            {availableSizes.length === 0 && (
                                                                <div className="text-white/60 text-xs">No variants</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {isSelectedMatchColor && (
                                                <div className="ml-auto px-2 py-1 rounded-sm bg-emerald-600/15 border border-emerald-400/50 text-[10px] font-semibold text-emerald-200 uppercase tracking-wide">
                                                    Match
                                                </div>
                                            )}
                                        </motion.div>
                                    </AnimatePresence>
                                </div>
                            )}
                            {showCheckout && (
                                <div className="p-3 sm:p-4 rounded-xl border border-white/10 bg-white/5 w-full">
                                    <div className="text-xs text-white/80 mb-2">Checkout</div>
                                    {/* Collapsible address section controlled by a button */}
                                    {!showAddressForm ? (
                                        <button type="button" onClick={() => setShowAddressForm(true)} className="w-full text-left mb-3 p-3 sm:p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition block">
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                <div className="text-[11px] text-white/60">Shipping address</div>
                                                <div className="text-[11px] text-white/70 flex items-center gap-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                                        <path d="M15.41 2.59a2 2 0 0 1 0 2.82l-9.5 9.5a2 2 0 0 1-.88.5l-3.04.76a.75.75 0 0 1-.91-.91l.76-3.04c.1-.36.28-.69.53-.95l9.5-9.5a2 2 0 0 1 2.82 0Zm-3.53 3.18-7.72 7.72c-.06.06-.1.13-.12.21l-.44 1.76 1.76-.44c.08-.02.15-.06.21-.12l7.72-7.72-1.41-1.41Zm2.12-2.12-.71.71 1.41 1.41.71-.71a1 1 0 1 0-1.41-1.41Z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="text-xs text-white/80 truncate" title={addressSummary || '—'}>
                                                {addressSummary || '—'}
                                            </div>
                                        </button>
                                    ) : (
                                        <div className="p-3 sm:p-4 mb-3 rounded-xl border border-white/10 bg-white/5 w-full">
                                            <div className="grid grid-cols-1 gap-2">
                                                <div className="text-[11px] text-white/60">Update your shipping address</div>
                                                <input className="px-2 py-1.5 rounded bg-black/40 border border-white/10 text-sm" placeholder="Full name" value={addr.full_name} onChange={(e) => setAddr(a => ({ ...a, full_name: e.target.value }))} />
                                                <input className="px-2 py-1.5 rounded bg-black/40 border border-white/10 text-sm" placeholder="Address line 1" value={addr.line1} onChange={(e) => setAddr(a => ({ ...a, line1: e.target.value }))} />
                                                <input className="px-2 py-1.5 rounded bg-black/40 border border-white/10 text-sm" placeholder="Address line 2 (optional)" value={addr.line2} onChange={(e) => setAddr(a => ({ ...a, line2: e.target.value }))} />
                                                <div className="grid grid-cols-2 gap-2">
                                                    <input className="px-2 py-1.5 rounded bg-black/40 border border-white/10 text-sm" placeholder="City" value={addr.city} onChange={(e) => setAddr(a => ({ ...a, city: e.target.value }))} />
                                                    <input className="px-2 py-1.5 rounded bg-black/40 border border-white/10 text-sm" placeholder="State/Province" value={addr.state} onChange={(e) => setAddr(a => ({ ...a, state: e.target.value }))} />
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <input className="px-2 py-1.5 rounded bg-black/40 border border-white/10 text-sm" placeholder="Postal code" value={addr.postal_code} onChange={(e) => setAddr(a => ({ ...a, postal_code: e.target.value }))} />
                                                    <input className="px-2 py-1.5 rounded bg-black/40 border border-white/10 text-sm" placeholder="Country" value={addr.country} onChange={(e) => setAddr(a => ({ ...a, country: e.target.value }))} />
                                                </div>
                                                <input className="px-2 py-1.5 rounded bg-black/40 border border-white/10 text-sm" placeholder="Phone (optional)" value={addr.phone} onChange={(e) => setAddr(a => ({ ...a, phone: e.target.value }))} />
                                                <div className="flex items-center gap-2">
                                                    <button onClick={saveAddress} disabled={savingAddr} className={`text-xs px-3 py-1.5 rounded-full border ${savingAddr ? 'border-white/10 text-white/40' : 'border-white/20 hover:border-white/40'} bg-white/10 hover:bg-white/20 transition`}>{savingAddr ? 'Saving…' : 'Save default'}</button>
                                                    <button onClick={resetToDefaultAddress} type="button" className="text-xs px-3 py-1.5 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 transition">Reset to default</button>
                                                    <button type="button" onClick={() => setShowAddressForm(false)} className="ml-auto text-[11px] px-3 py-1.5 rounded border border-white/20 bg-white/10 hover:bg-white/20">Done</button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="mt-3">
                                        <div className="text-xs text-white/80 mb-1">Payment</div>
                                        <div className="flex gap-3">
                                            {[{ id: 'card', label: 'Card' }, { id: 'paypal', label: 'PayPal' }, { id: 'crypto', label: 'Crypto' }].map(m => (
                                                <button
                                                    key={m.id}
                                                    type="button"
                                                    onClick={() => setPaymentMethod(m.id)}
                                                    className={`text-xs px-2 py-1.5 rounded border inline-flex items-center gap-1.5 ${paymentMethod === m.id ? 'border-indigo-500 bg-indigo-500/20' : 'border-white/20 hover:border-white/40 bg-white/10 hover:bg-white/20'}`}
                                                >
                                                    {m.id === 'card' ? (
                                                        <CreditCard className="w-4 h-4" />
                                                    ) : m.id === 'paypal' ? (
                                                        <FontAwesomeIcon icon={faPaypal} className="text-sm" />
                                                    ) : (
                                                        <Coins className="w-4 h-4" />
                                                    )}
                                                    <span>{m.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                        {paymentMethod === 'card' && (
                                            <div className="mt-2 text-[11px] text-white/70 flex items-center gap-2">
                                                <CreditCard className="w-4 h-4" />
                                                <span>Visa •••• 4242 • Exp 12/28</span>
                                            </div>
                                        )}
                                        {paymentMethod === 'paypal' && (
                                            <div className="mt-2 text-[11px] text-white/70 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faPaypal} className="text-sm" />
                                                <span>PayPal {effectiveUser?.email || 'user@example.com'}</span>
                                            </div>
                                        )}
                                        {paymentMethod === 'crypto' && !cryptoOpen && (
                                            <div className="mt-2 text-[11px] text-white/70 flex items-center gap-2">
                                                <Coins className="w-4 h-4" />
                                                <span>Crypto (BTC, ETH, USDC)</span>
                                            </div>
                                        )}
                                        {paymentMethod === 'crypto' && cryptoOpen && (
                                            <div className="mt-3 p-3 rounded-lg border border-white/10 bg-white/5">
                                                <div className="text-xs text-white/80 mb-3">Send payment to complete your order</div>
                                                <div className="grid grid-cols-2 gap-3 mb-3">
                                                    <div className="rounded-lg border border-white/10 bg-white/5 p-2">
                                                        <div className="text-[10px] text-white/70 mb-1">Network</div>
                                                        <div className="text-xs text-white">{cryptoInvoice?.network || 'Bitcoin'}</div>
                                                    </div>
                                                    <div className="rounded-lg border border-white/10 bg-white/5 p-2">
                                                        <div className="text-[10px] text-white/70 mb-1">Amount</div>
                                                        <div className="text-xs text-white">
                                                            {cryptoAmountLoading
                                                                ? 'Loading…'
                                                                : `${cryptoInvoice?.amount || '0.00000000'} ${cryptoInvoice?.currency || 'BTC'}`}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="rounded-lg border border-white/10 bg-white/5 p-2 mb-3">
                                                    <div className="text-[10px] text-white/70 mb-1">Address</div>
                                                    <div className="text-xs text-white font-mono break-all">{cryptoInvoice?.address}</div>
                                                </div>
                                                {Number.isFinite(cryptoInvoice?.usd_total) && (cryptoInvoice?.price_usd || cryptoAmountLoading) ? (
                                                    <div className="text-[10px] text-white/60 mb-3">
                                                        ≈ ${Number(cryptoInvoice.usd_total).toFixed(2)} USD • Rate: 1 BTC = {cryptoAmountLoading ? 'Loading…' : `$${Number(cryptoInvoice.price_usd).toLocaleString()} USD`}
                                                    </div>
                                                ) : null}
                                                {(() => {
                                                    const addr = cryptoInvoice?.address || '';
                                                    const amt = cryptoInvoice?.amount || '';
                                                    const cur = (cryptoInvoice?.currency || 'BTC').toUpperCase();
                                                    const scheme = cur === 'BTC' ? 'bitcoin' : cur.toLowerCase();
                                                    const uri = addr ? `${scheme}:${addr}${amt ? `?amount=${amt}` : ''}` : '';
                                                    const qrApi = uri ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(uri)}` : '';
                                                    return (
                                                        <div className="flex flex-col items-center justify-center gap-2 mb-3">
                                                            {qrApi ? (
                                                                <img src={qrApi} alt="Payment QR" className="w-32 h-32 rounded-md bg-white" />
                                                            ) : (
                                                                <div className="w-32 h-32 bg-gradient-to-br from-slate-300 to-slate-100 rounded-md shadow-inner" />
                                                            )}
                                                            {uri && (
                                                                <a
                                                                    href={uri}
                                                                    className="text-xs text-cyan-200 hover:text-cyan-100 underline"
                                                                    title="Open with your wallet"
                                                                >Open in wallet</a>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                                <div className="flex items-center justify-between gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setCryptoOpen(false);
                                                            setCryptoAmountLoading(false);
                                                            setOrderStatusMsg('Crypto payment cancelled.');
                                                        }}
                                                        className="px-3 py-2 rounded bg-white/10 hover:bg-white/20 border border-white/20 text-xs"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            setCryptoOpen(false);
                                                            await placeOrderNow();
                                                        }}
                                                        className="px-3 py-2 rounded bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-black text-xs font-semibold"
                                                    >
                                                        I've sent payment
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {orderStatusMsg && <div className="mt-2 text-xs text-white/80">{orderStatusMsg}</div>}
                                    {/* Order review appears only when user clicks Pay now - shown at bottom */}
                                    {showConfirmReview && (
                                        <div className="mt-4 p-3 sm:p-4 rounded-xl border border-white/10 bg-white/5">
                                            <div className="text-[11px] uppercase tracking-wide text-white/70 mb-2">Order review</div>
                                            {Array.isArray(reviewItems) && reviewItems.length > 0 ? (
                                                <div className="space-y-2 max-h-44 overflow-y-auto overflow-x-auto pr-3 scrollbar-thin">
                                                    {reviewItems.map(ci => {
                                                        const isSelectedExtra = !cartItems.some(i => String(i.id) === String(ci.id) && String(i.size || '') === String(ci.size || ''));
                                                        return (
                                                            <div key={`${ci.id}:${ci.size || 'M'}`} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors w-full">
                                                                <div className="w-14 h-14 rounded-md overflow-hidden bg-white/10 border border-white/10 flex-shrink-0">
                                                                    {ci.imageUrl ? (
                                                                        <img src={ci.imageUrl} alt={ci.display_name || ci.name} className="w-full h-full object-cover" />
                                                                    ) : (
                                                                        <div className="w-full h-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center">
                                                                            <div className="text-white/40 text-xs">No image</div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="text-sm font-medium text-white/90 mb-1 leading-tight">{ci.display_name || ci.name}</div>
                                                                    <div className="flex items-center gap-3 text-[11px] text-white/60 mb-2">
                                                                        <span>Size: {ci.size || 'N/A'}</span>
                                                                        <span>Qty: {ci.qty}</span>
                                                                    </div>
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="text-sm font-semibold text-white">{ci.currency || 'USD'} {(ci.price || 0)}</div>
                                                                        {isSelectedExtra ? (
                                                                            <button type="button" className="text-[11px] px-2 py-1 rounded border border-red-400/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 hover:text-red-200 transition-colors" onClick={() => setExcludeSelectedFromReview(true)}>Remove</button>
                                                                        ) : (
                                                                            <button type="button" className="text-[11px] px-2 py-1 rounded border border-white/20 bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors" onClick={() => removeItem(ci.id, ci.size)}>Remove</button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="text-[11px] text-white/60">Your cart is empty.</div>
                                            )}
                                            <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between">
                                                <div className="text-sm font-medium text-white/80">Total</div>
                                                <div className="text-lg font-bold text-white">{selectedProduct?.currency || 'USD'} {reviewTotal}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Other products from this player (excluding current) */}
                            {!showCheckout && !hideOtherSection && (
                                <div className="mt-3 sm:mt-4 pt-3 border-t border-white/10">
                                    <div className="text-[11px] uppercase tracking-wide text-white/70 mb-2">Other Products for {selectedPlayer?.name || 'Player'}</div>
                                    {Array.isArray(products) && products.filter(p => p.id !== selectedProduct.id).length === 0 ? (
                                        <div className="text-xs text-white/50">No other products</div>
                                    ) : (
                                        (() => {
                                            const groups = new Map();
                                            const getBaseId = (val) => {
                                                const s = String(val);
                                                return s.includes(':') ? s.split(':')[0] : s;
                                            };
                                            const selectedBaseId = selectedProduct ? getBaseId(selectedProduct._baseId || selectedProduct.id) : null;
                                            const others = (products || []).filter(p => String(p.id) !== String(selectedBaseId));

                                            // 1) Add jersey variants as first-level "Jerseys" group entries
                                            const jerseyVariants = [];
                                            // Include variants from the currently selected jersey too (except the chosen color)
                                            if (selectedProduct && isJerseyLike(selectedProduct)) {
                                                const selVars = Array.isArray(selectedProduct.variants) ? selectedProduct.variants : [];
                                                const selColors = Array.from(new Set(selVars.map(v => v.color).filter(Boolean)));
                                                for (const c of selColors) {
                                                    if (String(c) === String(selectedColor)) continue; // skip the current color
                                                    const v = selVars.find(x => String(x.color) === String(c));
                                                    const img = v?.image?.image_url || selectedProduct.image_url;
                                                    jerseyVariants.push({
                                                        ...selectedProduct,
                                                        id: `${selectedProduct.id}:${String(c)}`,
                                                        image_url: img,
                                                        display_name: `${selectedProduct.display_name || selectedProduct.name} • ${c}`,
                                                        _variantColor: c,
                                                        _baseId: selectedProduct.id,
                                                        _groupName: 'Jerseys',
                                                    });
                                                }
                                            }

                                            // Add variants from other jersey products
                                            for (const jp of others) {
                                                if (!isJerseyLike(jp)) continue;
                                                const variants = Array.isArray(jp.variants) ? jp.variants : [];
                                                const colors = Array.from(new Set(variants.map(v => v.color).filter(Boolean)));
                                                for (const c of colors) {
                                                    const v = variants.find(x => String(x.color) === String(c));
                                                    const img = v?.image?.image_url || jp.image_url;
                                                    jerseyVariants.push({
                                                        ...jp,
                                                        id: `${jp.id}:${String(c)}`,
                                                        image_url: img,
                                                        display_name: `${jp.display_name || jp.name} • ${c}`,
                                                        _variantColor: c,
                                                        _baseId: jp.id,
                                                        _groupName: 'Jerseys',
                                                    });
                                                }
                                            }
                                            if (jerseyVariants.length > 0) groups.set('Jerseys', jerseyVariants);

                                            // 2) Group the rest by existing categories (expand to color variants for non-jersey products)
                                            for (const p of others) {
                                                // Skip jersey base products here to avoid duplication; variants already listed above
                                                if (isJerseyLike(p)) continue;
                                                const cds = Array.isArray(p?.category_details) ? p.category_details : [];
                                                let names = [];
                                                if (cds.length > 0) {
                                                    const child = cds.filter(c => c && c.parent_id);
                                                    const top = cds.filter(c => c && !c.parent_id);
                                                    names = (child.length > 0 ? child : top).map(c => c.name || 'Other');
                                                } else {
                                                    // Fallback: attempt to map p.categories via any available categories cache; otherwise leave empty
                                                    const catIds = Array.isArray(p?.categories) ? p.categories : [];
                                                    const namesFromIds = [];
                                                    try {
                                                        const catMap = Array.isArray(categories)
                                                            ? Object.fromEntries(categories.map((c) => [c?.id, c]))
                                                            : {};
                                                        for (const cid of catIds) {
                                                            const cat = catMap[cid];
                                                            if (!cat) continue;
                                                            namesFromIds.push(cat.name || 'Other');
                                                        }
                                                    } catch { /* ignore */ }
                                                    names = namesFromIds;
                                                }
                                                const finalNames = names.length > 0 ? names : ['Other'];
                                                const seen = new Set();
                                                for (const n of finalNames) {
                                                    if (seen.has(n)) continue;
                                                    seen.add(n);
                                                    if (!groups.has(n)) groups.set(n, []);
                                                    const list = groups.get(n);
                                                    // Expand non-jersey products by color variants where available
                                                    const variants = Array.isArray(p?.variants) ? p.variants : [];
                                                    const colors = Array.from(new Set(variants.map(v => v.color).filter(Boolean)));
                                                    if (colors.length > 0) {
                                                        for (const c of colors) {
                                                            const v = variants.find(x => String(x.color) === String(c));
                                                            const img = v?.image?.image_url || p.image_url;
                                                            list.push({
                                                                ...p,
                                                                id: `${p.id}:${String(c)}`,
                                                                image_url: img,
                                                                display_name: `${p.display_name || p.name} • ${c}`,
                                                                price_cents: (v?.price_cents != null ? v.price_cents : p.price_cents),
                                                                currency: (v?.currency || p.currency),
                                                                _variantColor: c,
                                                                _baseId: p.id,
                                                            });
                                                        }
                                                    } else {
                                                        // Fallback: no variants, keep the product itself
                                                        list.push(p);
                                                    }
                                                }
                                            }
                                            const entries = Array.from(groups.entries());
                                            if (entries.length === 0) return null;
                                            return (
                                                <div className="space-y-2">
                                                    {entries.map(([subName, items]) => (
                                                        <div key={subName}>
                                                            <div className="text-[10px] uppercase tracking-wide text-white/60 mb-1">{subName}</div>
                                                            <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pr-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                                                                {items.map(p => (
                                                                    <button
                                                                        key={p.id || p.sku}
                                                                        type="button"
                                                                        onClick={() => openProductDetail(p, p._variantColor ? { preferColor: p._variantColor } : undefined)}
                                                                        className="snap-start shrink-0 w-20 sm:w-24 md:w-28 text-left rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 overflow-hidden transition-colors"
                                                                    >
                                                                        <div className="aspect-[4/5] bg-black/30">
                                                                            {p.image_url ? <img src={p.image_url} alt={p.display_name || p.name} className="w-full h-full object-cover" /> : null}
                                                                        </div>
                                                                        <div className="p-1.5">
                                                                            <div className="text-[11px] font-medium truncate">{p.display_name || p.name}</div>
                                                                            <div className="text-[10px] text-white/70">{p.currency} {((p.price_cents ?? 0) / 100).toFixed(2)}</div>
                                                                        </div>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })()
                                    )}
                                </div>
                            )}

                            {/* Recommended products */}
                            {!showCheckout && (
                                <div className="mt-3 sm:mt-4 pt-3 border-t border-white/10">
                                    <div className="text-[11px] uppercase tracking-wide text-white/70 mb-2">Recommended</div>
                                    {recsLoading && <div className="text-xs text-white/70">Loading…</div>}
                                    {!recsLoading && recs.length === 0 && (
                                        <div className="text-xs text-white/50">No recommendations</div>
                                    )}
                                    {!recsLoading && recs.length > 0 && (
                                        <div className="space-y-3">
                                            {recs.map((group, gi) => (
                                                <div key={`recg-${gi}`} className="space-y-2">
                                                    <div className="text-[11px] text-white/60 capitalize">{group.title || 'Recommended'}</div>
                                                    <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pr-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                                                        {group.items.map(r => (
                                                            <button
                                                                key={`${group.title || 'g'}-${r.sku || r.id}-${r.variant_id || ''}`}
                                                                type="button"
                                                                onClick={() => openProductDetail(r, r._variantColor ? { preferColor: r._variantColor } : undefined)}
                                                                className="snap-start shrink-0 w-28 sm:w-32 md:w-36 text-left rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 overflow-hidden transition-colors"
                                                            >
                                                                <div className="aspect-[4/5] bg-black/30">
                                                                    {r.image_url ? (
                                                                        <img src={r.image_url} alt={r.display_name || r.name} className="w-full h-full object-cover" />
                                                                    ) : null}
                                                                </div>
                                                                <div className="p-1.5">
                                                                    <div className="text-[11px] font-medium truncate">{r.display_name || r.name}</div>
                                                                    <div className="text-[10px] text-white/70">{r.currency} {((r.price_cents ?? 0) / 100).toFixed(2)}</div>
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* Sticky footer */}
                        <div className="sticky bottom-0 z-10 px-3 sm:px-4 py-2.5 sm:py-3 border-t border-white/10 bg-black/10 group-hover:bg-black/20 transition-colors flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    // Prevent immediate re-open triggered by selectedPlayer effect
                                    suppressAutoOpenRef.current = true;
                                    setSelectedProduct(null);
                                    setShowPanel(false);
                                    setSelectedPlayer(null);
                                    setShowCheckout(false);
                                }}
                                className="px-3 py-2 rounded bg-white/10 hover:bg-white/20 border border-white/20 text-sm"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    // Buy now should go directly to checkout with review since color/size are already selected
                                    if (!showCheckout) {
                                        setShowCheckout(true);
                                        setShowConfirmReview(true); // Always go to review for buy now
                                        return;
                                    }
                                    if (!showConfirmReview) {
                                        setShowConfirmReview(true);
                                        return;
                                    }
                                    // Handle crypto payment separately
                                    if (paymentMethod === 'crypto') {
                                        if (!cryptoOpen) {
                                            // Show crypto payment section inline
                                            (async () => {
                                                setCryptoAmountLoading(true);
                                                const demoAddr = 'bc1qdemoaddress000xyz...';
                                                const network = 'Bitcoin (Testnet)';
                                                const usdTotal = Number(reviewTotal);
                                                setCryptoInvoice({ address: demoAddr, network, amount: null, currency: 'BTC', price_usd: btcPriceUsd ?? null, usd_total: usdTotal });
                                                setCryptoOpen(true);
                                                try {
                                                    let price = btcPriceUsd;
                                                    if (!Number.isFinite(price) || price <= 0) {
                                                        const { data } = await apiClient.get('/crypto/btc/price');
                                                        const fetched = Number(data?.price_usd);
                                                        price = Number.isFinite(fetched) && fetched > 0 ? fetched : null;
                                                    }
                                                    const btcAmount = price ? usdTotal / price : 0;
                                                    setCryptoInvoice({ address: demoAddr, network, amount: btcAmount.toFixed(8), currency: 'BTC', price_usd: price, usd_total: usdTotal });
                                                } catch (e) {
                                                    setCryptoInvoice({ address: demoAddr, network, amount: '0.00000000', currency: 'BTC', price_usd: null, usd_total: usdTotal });
                                                } finally {
                                                    setCryptoAmountLoading(false);
                                                }
                                            })();
                                        }
                                        return;
                                    }
                                    placeOrderNow();
                                }}
                                disabled={placing}
                                className={`px-4 py-2 rounded ${placing ? 'bg-white/10 text-white/50 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'} text-sm`}
                            >
                                {placing ? 'Processing…' : (!showCheckout ? 'Buy now' : (!showConfirmReview ? 'Pay now' : 'Confirm & pay'))}
                            </button>
                            {!showCheckout && (
                                <button
                                    onClick={() => {
                                        const p = selectedProduct;
                                        const size = selectedSize || 'N/A';
                                        addItem({ id: p.id, name: p.name || p.display_name, price: p.price_cents ?? 0, currency: p.currency || 'USD', imageUrl: p.image_url || '', size, qty: 1 });
                                        setToastKind('success');
                                        setToastMsg('Added to cart');
                                        setTimeout(() => setToastMsg(''), 1800);
                                    }}
                                    disabled={Array.isArray(selectedVariants) && selectedVariants.length > 0 && !selectedSize}
                                    className={`px-4 py-2 rounded ${Array.isArray(selectedVariants) && selectedVariants.length > 0 && !selectedSize ? 'bg-white/10 text-white/50 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-sm`}
                                >
                                    Add to cart
                                </button>
                            )}
                        </div>

                        {/* overlay removed */}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Collapsed docked button to reopen the sidebar */}
            {selectedProduct && sidebarCollapsed && (
                <div className="fixed top-16 left-3 z-[2147483646]">
                    <button
                        type="button"
                        onClick={() => setSidebarCollapsed(false)}
                        className="px-3 py-2 rounded-lg border border-white/10 bg-black/20 hover:bg-black/30 text-white text-sm backdrop-blur-md"
                        title="Show product details"
                    >
                        Show products
                    </button>
                </div>
            )}

            <AnimatePresence>
                {toastMsg && (
                    <div className="fixed inset-0 pointer-events-none flex items-end justify-center pb-24 z-[2147483647]">
                        <motion.div
                            initial={{ y: 24, opacity: 0, scale: 0.98 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 24, opacity: 0, scale: 0.98 }}
                            transition={{ type: 'spring', stiffness: 480, damping: 34, mass: 0.6 }}
                            className="pointer-events-auto rounded-2xl px-4 py-3 shadow-2xl border backdrop-blur-md flex items-center gap-3"
                            style={{
                                background: 'linear-gradient(180deg, rgba(10,12,20,0.75) 0%, rgba(10,12,20,0.55) 100%)',
                                borderColor: 'rgba(255,255,255,0.12)'
                            }}
                        >
                            <div className="relative">
                                <div className={`absolute -inset-2 rounded-full blur-lg opacity-60 ${toastKind === 'success' ? 'bg-emerald-500/30' : 'bg-white/10'}`} />
                                <div className={`relative w-9 h-9 rounded-full shadow-lg ring-1 ring-white/20 flex items-center justify-center ${toastKind === 'success' ? 'bg-gradient-to-br from-emerald-500 to-green-600' : 'bg-white/10'}`}>
                                    <motion.svg
                                        key={`toast-icon-${toastKind}`}
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        className="w-5 h-5"
                                    >
                                        <motion.path
                                            d="M5 13l4 4L19 7"
                                            fill="none"
                                            stroke="white"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ duration: 0.55, ease: 'easeOut', delay: 0.05 }}
                                        />
                                    </motion.svg>
                                </div>
                            </div>
                            <div className="min-w-[160px] pr-1">
                                <div className="text-white font-medium leading-tight">
                                    {toastMsg}
                                </div>
                                <div className="text-[11px] text-white/70 leading-tight">
                                    {toastKind === 'success' ? 'All set!' : ''}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>


            <div ref={overlayRef} className="bg-[rgba(26,35,50,0.7)] border border-white/10 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 shadow-md overflow-visible relative group">
                <div className="flex items-center gap-2 sm:gap-3">
                    {/* Ticker content area (static row with animated tiles) */}
                    <div ref={trackRef} className="relative flex-1 min-w-0 overflow-hidden" style={{ height: tickerHeight }}>
                        {hasContent ? (
                            <div
                                className={`absolute inset-0 flex items-center gap-3 sm:gap-4 text-white font-semibold leading-tight ${fontClass}`}
                                style={{
                                    paddingRight: teamOptions.length >= 2 ? (isFullscreen ? '48px' : '50px') : '0px' // Less padding in fullscreen
                                }}
                            >
                                {tilesShown.map((entry, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        className="flex items-center gap-2 focus:outline-none"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // If a SKU was provided (series labels), resolve product by SKU first
                                            const sku = entry?.sku;
                                            if (sku && typeof sku === 'string' && sku.trim()) {
                                                (async () => {
                                                    try {
                                                        setDetailLoading(true);
                                                        const { data } = await apiClient.get(`/products/by-sku/${encodeURIComponent(sku)}?include_suggested=true`);
                                                        const prod = data?.product || null;
                                                        if (prod && prod.id) {
                                                            setSelectedProduct(null);
                                                            suppressAutoOpenRef.current = true;
                                                            // Regular product loading with team-based recommendations
                                                            setRecs(Array.isArray(data?.suggested_products) ? data.suggested_products : []);
                                                            setRecsLocked(false);
                                                            setHideOtherSection(false);
                                                            await openProductDetail(prod);
                                                            setShowPanel(false);
                                                        }
                                                    } catch (_) { /* ignore */ }
                                                    finally { setDetailLoading(false); }
                                                })();
                                                return;
                                            }
                                            const playerId = entry?.player_id;
                                            if (!playerId) return;
                                            setSelectedProduct(null);
                                            suppressAutoOpenRef.current = false;
                                            // Drive recommendation vendors from the clicked player's team
                                            try {
                                                const vendorKey = normalizeTeamKey(entry.display_team || entry.team || '');
                                                if (vendorKey) setRecVendors([vendorKey]);
                                            } catch { /* ignore */ }
                                            setRecs([]);
                                            setRecsLocked(false);
                                            setHideOtherSection(false);
                                            setSelectedPlayer({
                                                id: playerId,
                                                name: entry.display_name || entry.player || '',
                                                image_url: entry.image_url || ''
                                            });
                                            setShowPanel(false);
                                        }}
                                    >
                                        {(() => {
                                            const highlightFrame = highlightEnabled && highlightMap[entry.key];
                                            const teamFrame = getTeamFrame(entry);
                                            const frameClass = `${teamFrame.bgClass || ''} ${teamFrame.borderClass || ''} ${highlightFrame ? 'ring-2 ring-yellow-400' : ''}`;
                                            const badgeStyleBase = teamFrame.badgeStyle || null;
                                            return (
                                                <div className="flex items-center gap-2">
                                                    {(() => {
                                                        const w = `${avatarPx}px`;
                                                        const h = `${avatarPx}px`;
                                                        // First tile cube swap animation
                                                        const swapItem = swapBatch?.active ? (swapBatch.items || []).find(it => it.index === idx) : null;
                                                        if (swapItem) {
                                                            const fromImg = swapItem.from?.image_url;
                                                            const toImg = swapItem.to?.image_url;
                                                            return (
                                                                <div style={{ width: w, height: h, perspective: 1000, ...(teamFrame.bgStyle || {}) }} className={`rounded-md border ${frameClass} overflow-hidden relative shadow-lg`}>
                                                                    <motion.div
                                                                        key={swapItem.key}
                                                                        initial={{ rotateY: 0 }}
                                                                        animate={{ rotateY: -180 }}
                                                                        transition={{ duration: cubeMs / 1000, ease: 'easeInOut' }}
                                                                        style={{ width: '100%', height: '100%', transformStyle: 'preserve-3d', position: 'relative', transformOrigin: 'center center', willChange: 'transform' }}
                                                                    >
                                                                        {/* Front face (current) */}
                                                                        <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', transform: 'rotateY(0deg) translateZ(0.1px)' }}>
                                                                            {fromImg ? (
                                                                                <img src={fromImg} alt="player" className="block w-full h-full object-cover" />
                                                                            ) : (
                                                                                <div className="w-full h-full flex items-center justify-center">
                                                                                    <div className="w-3 h-3 rounded bg-white/50" />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {/* Back face (incoming) */}
                                                                        <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', transform: 'rotateY(180deg) translateZ(0.1px)' }}>
                                                                            {toImg ? (
                                                                                <img src={toImg} alt="player" className="block w-full h-full object-cover" />
                                                                            ) : (
                                                                                <div className="w-full h-full flex items-center justify-center">
                                                                                    <div className="w-3 h-3 rounded bg-white/50" />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </motion.div>
                                                                    {/* Inner frame overlay */}
                                                                    <div
                                                                        className="pointer-events-none absolute inset-0 rounded-md border-2"
                                                                        style={{ borderColor: 'rgba(255,255,255,0.4)' }}
                                                                        aria-hidden="true"
                                                                    />
                                                                </div>
                                                            );
                                                        }
                                                        // Normal tile (no swap active)
                                                        return (
                                                            <div className={`rounded-md border ${frameClass} overflow-hidden relative`} style={{ width: w, height: h, ...(teamFrame.bgStyle || {}) }}>
                                                                {entry.image_url ? (
                                                                    <img
                                                                        src={entry.image_url}
                                                                        alt={entry.display_name || entry.player || 'player'}
                                                                        className="block w-full h-full object-cover"
                                                                    />
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center">
                                                                        <div className="w-3 h-3 rounded bg-white/50" />
                                                                    </div>
                                                                )}
                                                                {/* Inner frame overlay */}
                                                                <div
                                                                    className="pointer-events-none absolute inset-0 rounded-md border-2"
                                                                    style={{ borderColor: 'rgba(255,255,255,0.4)' }}
                                                                    aria-hidden="true"
                                                                />
                                                            </div>
                                                        );
                                                    })()}
                                                    {(() => {
                                                        const fullName = (entry.display_name || entry.player || '').toString().trim();
                                                        const primary = formatShortName(fullName);
                                                        const team = (entry.display_team || entry.team || '').toString();
                                                        const num = entry.number != null ? String(entry.number) : (entry.n && entry.n.t ? String(entry.n.t) : '');
                                                        const swapItemTxt = swapBatch?.active ? (swapBatch.items || []).find(it => it.index === idx) : null;
                                                        if (swapItemTxt) {
                                                            const fromName = swapItemTxt.from?.display_name || swapItemTxt.from?.player || '';
                                                            const toName = swapItemTxt.to?.display_name || swapItemTxt.to?.player || '';
                                                            const fromTeam = swapItemTxt.from?.display_team || swapItemTxt.from?.team || '';
                                                            const toTeam = swapItemTxt.to?.display_team || swapItemTxt.to?.team || '';
                                                            const fromNum = swapItemTxt.from?.number != null ? String(swapItemTxt.from.number) : '';
                                                            const toNum = swapItemTxt.to?.number != null ? String(swapItemTxt.to.number) : '';
                                                            const fromPrimary = formatShortName(fromName);
                                                            const toPrimary = formatShortName(toName);
                                                            return (
                                                                <div
                                                                    className="relative flex flex-col min-w-0"
                                                                    style={{
                                                                        maxWidth: (() => {
                                                                            const avatarSize = isFullscreen ? 52 : 36;
                                                                            const gapSize = windowWidth >= 640 ? 28 : 20;
                                                                            const teamButtonSpace = teamOptions.length >= 2 ? (isFullscreen ? 52 : 44) : 0;
                                                                            const containerPadding = windowWidth >= 640 ? 24 : 16;
                                                                            // Responsive spacing calculation - more generous in fullscreen
                                                                            const safetyMargin = isFullscreen ? 15 : (windowWidth < 768 ? 30 : 25);
                                                                            const reducedGapSize = isFullscreen ? 20 : (windowWidth >= 640 ? 16 : 12);

                                                                            const availableWidth = windowWidth - containerPadding - teamButtonSpace - safetyMargin;
                                                                            const spaceForGaps = reducedGapSize * Math.max(0, dynamicMaxTiles - 1);
                                                                            const spaceForContent = Math.max(100, availableWidth - spaceForGaps);
                                                                            const spacePerTile = spaceForContent / dynamicMaxTiles;
                                                                            const textSpace = spacePerTile - avatarSize - (isFullscreen ? 8 : 6);

                                                                            // Less aggressive truncation in fullscreen
                                                                            let adjustedTextSpace;
                                                                            if (isFullscreen) {
                                                                                adjustedTextSpace = Math.min(textSpace * 0.9, 180); // Much more generous in fullscreen
                                                                            } else if (windowWidth < 480) {
                                                                                adjustedTextSpace = Math.min(textSpace * 0.5, 40);
                                                                            } else if (windowWidth < 640) {
                                                                                adjustedTextSpace = Math.min(textSpace * 0.6, 60);
                                                                            } else if (windowWidth < 768) {
                                                                                adjustedTextSpace = Math.min(textSpace * 0.7, 80);
                                                                            } else {
                                                                                adjustedTextSpace = Math.min(textSpace * 0.75, 120);
                                                                            }

                                                                            return `${Math.max(30, Math.floor(adjustedTextSpace))}px`;
                                                                        })()
                                                                    }}
                                                                >
                                                                    <motion.div
                                                                        key={`txt-old:${swapItemTxt.key}`}
                                                                        initial={{ opacity: 1 }}
                                                                        animate={{ opacity: 0 }}
                                                                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                                        className="absolute inset-0"
                                                                    >
                                                                        <span className="block truncate text-white text-xs sm:text-sm" title={fromPrimary}>{fromPrimary}</span>
                                                                        <div className="flex items-center gap-1.5 text-[10px] text-white/70 w-full min-w-0">
                                                                            <span className="truncate flex-1 min-w-0" title={fromTeam}>{fromTeam}</span>
                                                                            {fromNum ? (
                                                                                <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-sm font-semibold leading-tight text-[10px] sm:text-xs" style={badgeStyleBase || { border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.9)' }}>{fromNum}</span>
                                                                            ) : null}
                                                                        </div>
                                                                    </motion.div>
                                                                    <motion.div
                                                                        key={`txt-new:${swapItemTxt.key}`}
                                                                        initial={{ opacity: 0 }}
                                                                        animate={{ opacity: 1 }}
                                                                        transition={{ duration: 0.3, ease: 'easeInOut', delay: 0.1 }}
                                                                    >
                                                                        <span className="block truncate text-white text-xs sm:text-sm" title={toPrimary}>{toPrimary}</span>
                                                                        <div className="flex items-center gap-1.5 text-[10px] text-white/70 w-full min-w-0">
                                                                            <span className="truncate flex-1 min-w-0" title={toTeam}>{toTeam}</span>
                                                                            {toNum ? (
                                                                                <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-sm font-semibold leading-tight text-[10px] sm:text-xs" style={badgeStyleBase || { border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.9)' }}>{toNum}</span>
                                                                            ) : null}
                                                                        </div>
                                                                    </motion.div>
                                                                </div>
                                                            );
                                                        }
                                                        return (
                                                            <div
                                                                className="flex flex-col min-w-0"
                                                                style={{
                                                                    maxWidth: (() => {
                                                                        const avatarSize = isFullscreen ? 52 : 36;
                                                                        const gapSize = windowWidth >= 640 ? 28 : 20;
                                                                        const teamButtonSpace = teamOptions.length >= 2 ? (isFullscreen ? 52 : 44) : 0;
                                                                        const containerPadding = windowWidth >= 640 ? 24 : 16;
                                                                        // Responsive spacing calculation - more generous in fullscreen
                                                                        const safetyMargin = isFullscreen ? 15 : (windowWidth < 768 ? 30 : 25);
                                                                        const reducedGapSize = isFullscreen ? 20 : (windowWidth >= 640 ? 16 : 12);

                                                                        const availableWidth = windowWidth - containerPadding - teamButtonSpace - safetyMargin;
                                                                        const spaceForGaps = reducedGapSize * Math.max(0, dynamicMaxTiles - 1);
                                                                        const spaceForContent = Math.max(100, availableWidth - spaceForGaps);
                                                                        const spacePerTile = spaceForContent / dynamicMaxTiles;
                                                                        const textSpace = spacePerTile - avatarSize - (isFullscreen ? 8 : 6);

                                                                        // Less aggressive truncation in fullscreen
                                                                        let adjustedTextSpace;
                                                                        if (isFullscreen) {
                                                                            adjustedTextSpace = Math.min(textSpace * 0.9, 180); // Much more generous in fullscreen
                                                                        } else if (windowWidth < 480) {
                                                                            adjustedTextSpace = Math.min(textSpace * 0.5, 40);
                                                                        } else if (windowWidth < 640) {
                                                                            adjustedTextSpace = Math.min(textSpace * 0.6, 60);
                                                                        } else if (windowWidth < 768) {
                                                                            adjustedTextSpace = Math.min(textSpace * 0.7, 80);
                                                                        } else {
                                                                            adjustedTextSpace = Math.min(textSpace * 0.75, 120);
                                                                        }

                                                                        return `${Math.max(30, Math.floor(adjustedTextSpace))}px`;
                                                                    })()
                                                                }}
                                                            >
                                                                <span className={`block truncate text-white leading-tight ${isFullscreen ? 'text-sm' : 'text-xs'}`} title={primary}>{primary}</span>
                                                                <div className={`flex items-center gap-1 text-white/70 w-full min-w-0 ${isFullscreen ? 'text-[10px]' : 'text-[9px]'}`}>
                                                                    <span className="truncate flex-1 min-w-0" title={team}>{team}</span>
                                                                    {num ? (
                                                                        <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-sm font-semibold leading-tight text-[10px] sm:text-xs" style={badgeStyleBase || { border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.9)' }}>{num}</span>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            );
                                        })()}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="absolute inset-0 flex items-center text-white text-xs sm:text-sm">
                                Live Metadata
                            </div>
                        )}
                        {/* Right-side team filter toggle inside the track (centered vertically) */}
                        {teamOptions.length >= 2 && (
                            <div ref={teamToggleRef} className="absolute right-0 top-1/2 -translate-y-1/2 z-20 pointer-events-none">
                                {(() => {
                                    const t1 = teamOptions[0];
                                    const t2 = teamOptions[1];
                                    const toRgba = (color, a = 0.55) => {
                                        const hex = (color || '').toString();
                                        const h = hex.replace('#', '');
                                        if (/^[0-9a-fA-F]{3}$/.test(h)) {
                                            const r = parseInt(h[0] + h[0], 16);
                                            const g = parseInt(h[1] + h[1], 16);
                                            const b = parseInt(h[2] + h[2], 16);
                                            return `rgba(${r}, ${g}, ${b}, ${a})`;
                                        }
                                        if (/^[0-9a-fA-F]{6}$/.test(h)) {
                                            const r = parseInt(h.slice(0, 2), 16);
                                            const g = parseInt(h.slice(2, 4), 16);
                                            const b = parseInt(h.slice(4, 6), 16);
                                            return `rgba(${r}, ${g}, ${b}, ${a})`;
                                        }
                                        const named = hex.toLowerCase();
                                        if (named.includes('yellow')) return 'rgba(234,179,8,0.55)';
                                        if (named.includes('green')) return 'rgba(34,197,94,0.55)';
                                        if (named.includes('blue')) return 'rgba(59,130,246,0.55)';
                                        if (named.includes('red')) return 'rgba(239,68,68,0.55)';
                                        return 'rgba(255,255,255,0.35)';
                                    };
                                    const segmentBase = 'px-3 py-1 text-[10px] sm:text-xs font-semibold focus:outline-none select-none flex items-center gap-1';
                                    const leftActive = teamFilter && normalizeTeamKey(teamFilter) === normalizeTeamKey(t1.key);
                                    const rightActive = teamFilter && normalizeTeamKey(teamFilter) === normalizeTeamKey(t2.key);
                                    const midActive = !teamFilter;
                                    // Mini dot indicator (always visible, hides on hover)
                                    const t1Logo = teamLogoMapRef.current.get(normalizeTeamKey(t1.key));
                                    const t2Logo = teamLogoMapRef.current.get(normalizeTeamKey(t2.key));
                                    const dotStyle = (() => {
                                        // For Both, always show split color gradient (no logos)
                                        if (midActive) {
                                            return {
                                                backgroundImage: `linear-gradient(135deg, ${toRgba(t1.color, 0.9)} 0%, ${toRgba(t1.color, 0.9)} 50%, ${toRgba(t2.color, 0.9)} 50%, ${toRgba(t2.color, 0.9)} 100%)`
                                            };
                                        }
                                        // Single team selected: logo if available, otherwise color
                                        if (leftActive || rightActive) {
                                            const c = leftActive ? t1.color : t2.color;
                                            return { backgroundColor: toRgba(c, 0.9) };
                                        }
                                        return {};
                                    })();
                                    return (
                                        <>
                                            {!teamToggleOpen && (
                                                <button
                                                    ref={teamToggleRef}
                                                    type="button"
                                                    onClick={() => {
                                                        setTeamDropdownStyle(computeTeamDropdownStyle());
                                                        setTeamToggleOpen(true);
                                                    }}
                                                    className="mr-1.5 w-8 h-8 rounded-full border border-white/30 shadow focus:outline-none bg-black/20 overflow-hidden flex items-center justify-center pointer-events-auto"
                                                    style={dotStyle}
                                                    aria-label="Filter teams"
                                                    title="Filter teams"
                                                >
                                                    {(() => {
                                                        if (midActive) {
                                                            return null; // colors-only in Both state
                                                        } else if (leftActive && t1Logo) {
                                                            return (<img src={t1Logo} alt="Team 1" className="w-full h-full object-contain p-1" />);
                                                        } else if (rightActive && t2Logo) {
                                                            return (<img src={t2Logo} alt="Team 2" className="w-full h-full object-contain p-1" />);
                                                        }
                                                        return null;
                                                    })()}
                                                </button>
                                            )}
                                            {/* dropdown menu moved outside wrapper for proper pointer-events */}
                                        </>
                                    );
                                })()}
                            </div>
                        )}
                        {teamToggleOpen && (() => {
                            const opts = (teamOptions || []).slice(0,2);
                            const t1 = opts[0] || teamOptions[0];
                            const t2 = opts[1] || teamOptions[1] || opts[0];
                            const t1Logo = teamLogoMapRef.current.get(normalizeTeamKey(t1.key));
                            const t2Logo = teamLogoMapRef.current.get(normalizeTeamKey(t2.key));
                            return (
                                <div ref={teamDropdownRef} className="fixed z-[2147483647] rounded-lg border border-white/10 bg-[rgba(26,35,50,0.85)] backdrop-blur-md shadow-lg overflow-hidden min-w-[9.5rem] pointer-events-auto"
                                    style={{ right: (teamDropdownStyle?.right ?? 16), bottom: (teamDropdownStyle?.bottom ?? 80) }}
                                    onMouseDown={(e) => { e.stopPropagation(); }}>
                                    <button type="button" className={`w-full px-3 py-2 text-left text-white flex items-center gap-2 hover:bg-white/10 ${teamFilter && normalizeTeamKey(teamFilter) === normalizeTeamKey(t1.key) ? 'bg-white/10 ring-1 ring-white/40' : ''}`}
                                        onClick={() => { setTeamFilter(t1.key); setTeamToggleOpen(false); }} title={t1.label || (t1.key || '').toUpperCase()}>
                                        {t1Logo ? (
                                            <img src={t1Logo} alt="Team 1" className="inline-block w-5 h-5 rounded-full object-contain bg-white/10 p-0.5" />
                                        ) : (
                                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.85)' }} aria-hidden />
                                        )}
                                        <span className="drop-shadow">{t1.label || (t1.key || '').toUpperCase()}</span>
                                    </button>
                                    <button type="button" className={`w-full px-3 py-2 text-left text-white flex items-center gap-2 hover:bg-white/10 ${!teamFilter ? 'bg-white/10 ring-1 ring-white/40' : ''}`}
                                        onClick={() => { setTeamFilter(null); setTeamToggleOpen(false); }} title="Show both">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0.75) 100%)' }} aria-hidden />
                                        <span className="drop-shadow">Both</span>
                                    </button>
                                    <button type="button" className={`w-full px-3 py-2 text-left text-white flex items-center gap-2 hover:bg-white/10 ${teamFilter && normalizeTeamKey(teamFilter) === normalizeTeamKey(t2.key) ? 'bg-white/10 ring-1 ring-white/40' : ''}`}
                                        onClick={() => { setTeamFilter(t2.key); setTeamToggleOpen(false); }} title={t2.label || (t2.key || '').toUpperCase()}>
                                        {t2Logo ? (
                                            <img src={t2Logo} alt="Team 2" className="inline-block w-5 h-5 rounded-full object-contain bg-white/10 p-0.5" />
                                        ) : (
                                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.75)' }} aria-hidden />
                                        )}
                                        <span className="drop-shadow">{t2.label || (t2.key || '').toUpperCase()}</span>
                                    </button>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WatchBottomMetadataOverlay;
